import { Link } from "@material-ui/core";
import { WarningOutlined } from "@material-ui/icons";
import { TextareaAutosize, ToggleButton, ToggleButtonGroup, Snackbar, Alert } from "@mui/material";
import axios from "axios";
import moment from "moment";
import { use, useEffect, useMemo, useRef, useState } from "react";
import { Button, Dropdown } from "react-bootstrap";
import { DropdownSubmenu } from "react-bootstrap-submenu";
import Modal from 'react-bootstrap/Modal';
import { GrDocumentText } from "react-icons/gr";
import { useDispatch, useSelector } from "react-redux";
import { Link as RouterLink } from "react-router-dom";
import ReactSelect from "react-select";
import { fetchRequestStatuses, initLenderRequest } from "../../../../redux/actions/lenderRequestActions";
import { isAdminRole, isInternal, isLenderRole, isProjectReadOnlyRole, isProjectRole, isProjectStandardRole, isReadonlyRole, isTPORole } from "../../../../redux/actions/userAction";
import { getEnvName } from "../../../shared/constants/environment";
import { FM_STATUSES } from "../../../shared/constants/FMStatuses";
import { LINE_ITEM_STATUS, REQUEST_NOTES, REQUEST_STATUSES, isClosedStatus, isLenderActionableStatus, isReviewableStatus } from "../../../shared/constants/RequestStatuses";
import { REQUEST_TYPES } from "../../../shared/constants/RequestTypes";
import { PROJECT_TYPES } from "../../../shared/constants/ProjectTypes";
import { downloadFile, getAdditionalDocuments } from "../../../shared/services/LenderRequestService";
import { getGoogleAddressFields } from "../../../shared/services/SharedService";
import DatePicker from "../../../SharedComponents/DatePicker/DatePicker.jsx";
import ReasonCodeTooltip from "../../../SharedComponents/ReasonCodeTooltip/ReasonCodeTooltip";
import EditableRequestField from "./EditableRequestField";
import ReasonCodesReviewSection from "./ReasonCodesReviewSection";
import { DelayFor } from "../../../shared/utilities/promise-utils.js";
import DeliveryRestrictionsReviewSection from "../RemoveDeliveryRestrictions/DeliveryRestrictionsReviewSection.js";
import { difference } from "../../../shared/utilities/array-utils.js";
import { selectCurrentRestrictions } from "../../../../redux/features/lender-request/removeDeliveryRestrictionSlice.js";
import { requestStatusesSelector } from "../../../../redux/reducers/lenderRequestReducer.js";
import { isNumeric } from "../../../shared/utilities/number-utils";

import "./RequestDetailsModal.css";
import ProjectInfoEditorAccordion from "../../../SharedComponents/ProjectInfoEditorAccordion/ProjectInfoEditorAccordion.js";

// Resolves the currently-in-effect Project Type across edited/fetched/original project profile sources
const getCurrentProjectType = (editedProjectProfile, fetchedProjectProfile, projectInfo) =>
  editedProjectProfile?.newProjectType ?? fetchedProjectProfile?.newProjectType ?? projectInfo?.projectProfile?.newProjectType ?? projectInfo?.newProjectType;

// coerce "true"/"false" back to booleans
function parseBoolean(value) {
  return value === "false"
    ? false
    : value === "true"
    ? true
    : value;
}

function createFallbackFieldDef(fieldName) {
  return {
    fieldName,
    type: "text",
    required: false,
    formatLabel: () => fieldName,
    formatValue: value => value || "(none)",
  };
}

// The canned restrictions all have names with the pattern "Restriction 1", "Restriction 2", "Restriction 3", etc.
// We want to sort them numerically instead of alphabetically.
const restrictionComparer = (rest1, rest2) => {
  const getRestrictionNumber = restriction => {
    const name = restriction?.restrictionName ?? restriction?.restrictionsName ?? restriction?.referenceName ?? "";
    const match = /Restriction (\d+)/.exec(name)?.[1];
    
    return match && !restriction.customIndex 
      ? +match 
      : Number.MAX_VALUE;
  };

  return rest1.customIndex && !rest2.customIndex ? 1
        : !rest1.customIndex && rest2.customIndex ? -1
        : getRestrictionNumber(rest1) > getRestrictionNumber(rest2) ? 1
        : getRestrictionNumber(rest1) < getRestrictionNumber(rest2) ? -1
    : (rest1?.restrictionName ?? rest1?.restrictionsName ?? rest1?.referenceName ?? "") > (rest2?.restrictionName ?? rest2?.restrictionsName ?? rest2?.referenceName ?? "") ? 1
    : (rest1?.restrictionName ?? rest1?.restrictionsName ?? rest1?.referenceName ?? "") < (rest2?.restrictionName ?? rest2?.restrictionsName ?? rest2?.referenceName ?? "") ? -1
        : 0;
}

const RequestDetailsModal = ({ show, onClose, request, projectInfo, projectRestrictions, activityFeed, isProjectDeleted = false }) => {
  // Derive Status Note display text from the most recent activity event
  const mostRecentEvent = activityFeed?.[0];
  const shouldShowExternalNote = !isInternal() && !isProjectReadOnlyRole() && mostRecentEvent?.notesIsExternal;
  
  let statusNoteText = "(none)";
  if (isInternal() || isProjectReadOnlyRole()) {
    // Internal/ProjectReadOnly users: always show the raw note text
    const rawNote = mostRecentEvent?.notes?.trim() || "";
    statusNoteText = rawNote || "(none)";
  } else if (shouldShowExternalNote) {
    // External users: apply substitution rule for "Additional Information Received"
    if (mostRecentEvent.internalStatus === REQUEST_STATUSES.ADDITIONAL_INFORMATION_RECEIVED) {
      statusNoteText = REQUEST_NOTES.ADDITIONAL_DOCUMENTS_SUBMITTED;
    } else {
      statusNoteText = mostRecentEvent.notes?.trim() || "(none)";
    }
  }

  const [internalStatusName, setInternalStatusName] = useState("");
  const [statusName, setStatusName] = useState("");
  const [stagingChanges, setStagingChanges] = useState([]);
  const [originalChanges, setOriginalChanges] = useState();
  const [addedPhases, setAddedPhases] = useState();
  const [originalAddedPhases, setOriginalAddedPhases] = useState();
  const [documents, setDocuments] = useState([]);
  const [note, setNote] = useState("");
  const [notesExternal, setNotesExternal] = useState(false);
  const [notesBoxActive, setNotesBoxActive] = useState(false);
  const [requestedDocuments, setRequestedDocuments] = useState([]);
  const [lineItemApprovals, setLineItemApprovals] = useState(new Map());
  const [additionalDocuments, setAdditionalDocuments] = useState([]);
  const [availableDocuments, setAvailableDocuments] = useState([]);
  
  const [availableFnmaStatuses, setAvailableFnmaStatuses] = useState([]);
  const [newFNMAStatus, setNewFNMAStatus] = useState("");
  const [showFnmaStatusDropdown, setShowFnmaStatusDropdown] = useState(false);
  const [approvalExpiryDate, setApprovalExpiryDate] = useState(null);
  const [suspensionReason, setSuspensionReason] = useState("");
  const [sellingServicingGuideRef, setSellingServicingGuideRef] = useState("");
  const [reviewedProjectExhibits, setReviewedProjectExhibits] = useState("");
  const [reasonForIneligibility, setReasonForIneligibility] = useState("");
  
  const [restrictions, setRestrictions] = useState([]);
  const [addedRestrictions, setAddedRestrictions] = useState([]);
  const [canAddRestrictions, setCanAddRestrictions] = useState(false);

  // Remove Delivery Restriction review state
  const [deliveryRestrictionDecisions, setDeliveryRestrictionDecisions] = useState([]);
  const [canReviewRestrictions, setCanReviewRestrictions] = useState(false);
  const [fetchedRequestedRestrictionIds, setFetchedRequestedRestrictionIds] = useState(null);
  const [fetchedActiveRestrictionIds, setFetchedActiveRestrictionIds] = useState(null);
  const [fetchedRemoveDRTrackers, setFetchedRemoveDRTrackers] = useState([]);

  const [allReasonCodes, setAllReasonCodes] = useState([]);
  const [reasonCodeOptions, setReasonCodeOptions] = useState([]);
  const [addedReasonCodes, setAddedReasonCodes] = useState(request.reasonCodeDTOList || []);
  const [reasonCodeIds, setReasonCodeIds] = useState(new Set());
  const [canAddReasonCodes, setCanAddReasonCodes] = useState(false);
  const [shouldShowReasonCodes, setShouldShowReasonCodes] = useState(false);
  const [activeReasonCodes, setActiveReasonCodes] = useState([]); // Active reason codes from project's FM status

  // Modify Unavailable Status delta review model (IMPL-5/6) — gated on data.activeReasonCodes from
  // fetch-status-change-requests; falls back to legacy behavior when absent/empty (R8).
  const [useReasonCodeDeltaModel, setUseReasonCodeDeltaModel] = useState(false);
  const [deltaActiveReasonCodeIds, setDeltaActiveReasonCodeIds] = useState([]);
  const [deltaReasonCodeChanges, setDeltaReasonCodeChanges] = useState([]);
  const [reasonCodeDecisions, setReasonCodeDecisions] = useState([]);

  const [hasPersDecision, setHasPersDecision] = useState(false);
  const [isLoadingStatusChangeData, setIsLoadingStatusChangeData] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Snackbar state for error messages
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  
  const [canReview, setCanReview] = useState(false);
  const [canSubmit, setCanSubmit] = useState(false);
  const [editedProjectProfile, setEditedProjectProfile] = useState(null);
  const [fetchedProjectProfile, setFetchedProjectProfile] = useState(null);
  const [projectProfileValid, setProjectProfileValid] = useState(true);
  const [projectProfileTouched, setProjectProfileTouched] = useState(false);
  const [projectProfileHasChanges, setProjectProfileHasChanges] = useState(false);
  const [editingItems, setEditingItems] = useState(new Set());
  const [validationErrors, setValidationErrors] = useState(new Map());
  // Autocomplete-selection-driven address updates for Add Project (bypasses stagingChanges, see handleAddressFieldsUpdate)
  const [autocompleteFieldUpdates, setAutocompleteFieldUpdates] = useState({});
  const [accordionExpanded, setAccordionExpanded] = useState(false);
  const [phaseEditingItems, setPhaseEditingItems] = useState(new Map());
  const [phaseValidationErrors, setPhaseValidationErrors] = useState(new Map());
  const [phaseName, setPhaseName] = useState();
  const [phaseId, setPhaseId] = useState();
  const [bypassDuplicateProjectId, setBypassDuplicateProjectId] = useState(null);
  const [bypassDuplicateMessage, setBypassDuplicateMessage] = useState(null);
  const baselineStatusRef = useRef("");

  // todo: eliminate phaseLineItemApprovals and handlePhaseLineItemDecision since we are
  // setting Accept/Reject/Modify status on the entire phase
  const [phaseLineItemApprovals, setPhaseLineItemApprovals] = useState(new Map());
  const reviewedStatuses = new Set([
    REQUEST_STATUSES.ACTION_TAKEN,
    REQUEST_STATUSES.PARTIAL_ACTION_TAKEN,
    REQUEST_STATUSES.NO_ACTION_TAKEN,
  ]);

  const currentProjectType = getCurrentProjectType(editedProjectProfile, fetchedProjectProfile, projectInfo);
  const isReadOnlyMode = !canReview || !reviewedStatuses.has(statusName);
  // undefined sentinel is distinct from any real Project Type string, so the cascade always runs on first resolution
  const previousProjectTypeRef = useRef(undefined);
  // tracks whether addedPhases was already populated, so the cascade also fires when phases arrive after Project Type already resolved
  const previousAddedPhasesLoadedRef = useRef(false);

  useEffect(() => {
    if (request.requestTypeName === REQUEST_TYPES.LENDER_ADD_PROJECT) {
      const projectTypeChanged = currentProjectType !== previousProjectTypeRef.current;
      const phasesJustLoaded = !!addedPhases?.length && !previousAddedPhasesLoadedRef.current;
      if (projectTypeChanged || phasesJustLoaded) {
        if (currentProjectType === PROJECT_TYPES.ESTABLISHED_CONDO) {
          setAddedPhases(prev => prev?.map(p => ({ ...p, status: LINE_ITEM_STATUS.REJECT })));
        } else {
          setAddedPhases(prev => prev?.map(p => ({ ...p, status: LINE_ITEM_STATUS.ACCEPT })));
        }
      }
    }
    previousProjectTypeRef.current = currentProjectType;
    previousAddedPhasesLoadedRef.current = !!addedPhases?.length;
  }, [currentProjectType, request.requestTypeName, addedPhases]);

  // TODO: select project or phase field defs during initLenderRequest based on request type
  const projectFieldsMap = useSelector(state => state.lenderRequestReducer.projectFieldsMap);  
  const phaseFieldsMap = useSelector(state => state.lenderRequestReducer.phaseFieldsMap);  
  const requestStatuses = useSelector(requestStatusesSelector);
  const restrictionsByProjectId = useSelector(state => state.restrictionReducer.restrictionsByProjectId);
  const allDocTypesById = useSelector(state => state.lenderRequestReducer.allDocTypesById);

  const currentRestrictions = useSelector(selectCurrentRestrictions);

  const dispatch = useDispatch();

  // Resolves the internal-facing display label for a given backend statusName
  // by looking it up in the requestStatuses list from the API.
  // Falls back to the provided fallback value (e.g. internalStatusName) or empty string.
  const getInternalDisplayStatus = (status, fallback) => {
    if (status) {
      const match = requestStatuses?.find(s => s.statusName === status);
      if (match?.internalStatus) return match.internalStatus;
      return status;
    }
    return fallback || "";
  };

  // Refs for Google Autocomplete
  const streetInputRef = useRef(null);
  const cityInputRef = useRef(null);
  const zip5InputRef = useRef(null);
  const autocompleteRef = useRef(null);
  const cityAutocompleteRef = useRef(null);
  const zip5AutocompleteRef = useRef(null);
  const phaseStreetRefs = useRef({});
  const phaseAutocompleteRefs = useRef({});

  // Initialize Google Autocomplete for street field in Edit Project Requests
  useEffect(() => {
    const isEditing = editingItems.has("street") || (request.requestTypeName === REQUEST_TYPES.LENDER_ADD_PROJECT && accordionExpanded);

    if (!isEditing || !streetInputRef?.current) {
      if (autocompleteRef.current && window.google?.maps?.event) {
        window.google.maps.event.clearInstanceListeners(autocompleteRef.current);
        autocompleteRef.current = null;
      }
      return;
    }

    if (!window.google?.maps?.places) {
      console.warn('Google Maps Places API not loaded');
      return;
    }

    try {
      const autocomplete = new window.google.maps.places.Autocomplete(streetInputRef.current, {
        componentRestrictions: { country: 'US' },
        fields: ["address_components"],
        types: ['address'],
      });

      autocomplete.addListener('place_changed', () => {
        const place = autocomplete.getPlace();
        if (!place?.address_components) return;
        const addr = getGoogleAddressFields(place);
        if (request.requestTypeName === REQUEST_TYPES.LENDER_ADD_PROJECT) {
          setAutocompleteFieldUpdates({ street: addr.street, city: addr.city, state: addr.state, zip5: addr.zip });
        } else {
          handleAddressFieldsUpdate({ street: addr.street, city: addr.city, state: addr.state, zip5: addr.zip });
        }
      });

      autocompleteRef.current = autocomplete;
    } catch (error) {
      console.error('Error initializing street Google Autocomplete:', error);
    }

    return () => {
      if (autocompleteRef.current && window.google?.maps?.event) {
        window.google.maps.event.clearInstanceListeners(autocompleteRef.current);
        autocompleteRef.current = null;
      }
    };
  }, [editingItems.has("street"), request.requestTypeName, accordionExpanded]);

  // Initialize Google Autocomplete for city field in Edit Project Requests
  useEffect(() => {
    const isEditing = editingItems.has("city") || (request.requestTypeName === REQUEST_TYPES.LENDER_ADD_PROJECT && accordionExpanded);

    if (!isEditing || !cityInputRef?.current) {
      if (cityAutocompleteRef.current && window.google?.maps?.event) {
        window.google.maps.event.clearInstanceListeners(cityAutocompleteRef.current);
        cityAutocompleteRef.current = null;
      }
      return;
    }

    if (!window.google?.maps?.places) {
      console.warn('Google Maps Places API not loaded');
      return;
    }

    try {
      const autocomplete = new window.google.maps.places.Autocomplete(cityInputRef.current, {
        componentRestrictions: { country: 'US' },
        fields: ["address_components"],
        types: ['(cities)'],
      });

      autocomplete.addListener('place_changed', () => {
        const place = autocomplete.getPlace();
        if (!place?.address_components) return;
        const addr = getGoogleAddressFields(place);
        if (request.requestTypeName === REQUEST_TYPES.LENDER_ADD_PROJECT) {
          setAutocompleteFieldUpdates({ city: addr.city, state: addr.state });
        } else {
          handleAddressFieldsUpdate({ city: addr.city, state: addr.state });
        }
      });

      cityAutocompleteRef.current = autocomplete;
    } catch (error) {
      console.error('Error initializing city Google Autocomplete:', error);
    }

    return () => {
      if (cityAutocompleteRef.current && window.google?.maps?.event) {
        window.google.maps.event.clearInstanceListeners(cityAutocompleteRef.current);
        cityAutocompleteRef.current = null;
      }
    };
  }, [editingItems.has("city"), request.requestTypeName, accordionExpanded]);

  // Initialize Google Autocomplete for zip5 field in Edit Project Requests
  useEffect(() => {
    const isEditing = editingItems.has("zip5") || (request.requestTypeName === REQUEST_TYPES.LENDER_ADD_PROJECT && accordionExpanded);

    if (!isEditing || !zip5InputRef?.current) {
      if (zip5AutocompleteRef.current && window.google?.maps?.event) {
        window.google.maps.event.clearInstanceListeners(zip5AutocompleteRef.current);
        zip5AutocompleteRef.current = null;
      }
      return;
    }

    if (!window.google?.maps?.places) {
      console.warn('Google Maps Places API not loaded');
      return;
    }

    try {
      const autocomplete = new window.google.maps.places.Autocomplete(zip5InputRef.current, {
        componentRestrictions: { country: 'US' },
        fields: ["address_components"],
        types: ['postal_code'],
      });

      autocomplete.addListener('place_changed', () => {
        const place = autocomplete.getPlace();
        if (!place?.address_components) return;
        const addr = getGoogleAddressFields(place);
        if (request.requestTypeName === REQUEST_TYPES.LENDER_ADD_PROJECT) {
          setAutocompleteFieldUpdates({ zip5: addr.zip, city: addr.city, state: addr.state });
        } else {
          handleAddressFieldsUpdate({ zip5: addr.zip, city: addr.city, state: addr.state });
        }
      });

      zip5AutocompleteRef.current = autocomplete;
    } catch (error) {
      console.error('Error initializing ZIP Google Autocomplete:', error);
    }

    return () => {
      if (zip5AutocompleteRef.current && window.google?.maps?.event) {
        window.google.maps.event.clearInstanceListeners(zip5AutocompleteRef.current);
        zip5AutocompleteRef.current = null;
      }
    };
  }, [editingItems.has("zip5"), request.requestTypeName, accordionExpanded]);

  // Initialize Google Autocomplete for street fields in phases
  useEffect(() => {
    if (!addedPhases?.length || !window.google?.maps?.places) {
      return;
    }

    addedPhases.forEach((phase, phaseIndex) => {
      const isEditing = phaseEditingItems.get(phaseIndex)?.has("street");
      const inputRef = phaseStreetRefs.current[phaseIndex];
      
      if (!isEditing || !inputRef) {
        // Cleanup autocomplete for this phase if editing stopped
        if (phaseAutocompleteRefs.current[phaseIndex]) {
          if (window.google?.maps?.event) {
            window.google.maps.event.clearInstanceListeners(phaseAutocompleteRefs.current[phaseIndex]);
          }
          delete phaseAutocompleteRefs.current[phaseIndex];
        }
        return;
      }

      // Skip if autocomplete already initialized for this phase
      if (phaseAutocompleteRefs.current[phaseIndex]) {
        return;
      }

      try {
        const autocompleteConfig = {
          componentRestrictions: { country: 'US' },
          fields: ["address_components", "geometry"],
          types: ['address']
        };

        const autocomplete = new window.google.maps.places.Autocomplete(inputRef, autocompleteConfig);

        autocomplete.addListener('place_changed', () => {
          const place = autocomplete.getPlace();
          if (!place?.address_components) return;

          const address = getGoogleAddressFields(place);
          
          if (address.street) {
            handlePhaseValueModified(phaseIndex, "street", address.street, null);
          }
        });

        phaseAutocompleteRefs.current[phaseIndex] = autocomplete;
      } catch (error) {
        console.error('Error initializing Google Autocomplete for phase:', error);
      }
    });

    return () => {
      // Cleanup all phase autocompletes
      Object.values(phaseAutocompleteRefs.current).forEach(autocomplete => {
        if (autocomplete && window.google?.maps?.event) {
          window.google.maps.event.clearInstanceListeners(autocomplete);
        }
      });
      phaseAutocompleteRefs.current = {};
    };
  }, [addedPhases, phaseEditingItems]);

  useEffect(() => {
    if (show && request && projectInfo) {
      dispatch(initLenderRequest(request.requestTypeName));
      dispatch(fetchRequestStatuses());
      if (request.requestTypeName === REQUEST_TYPES.REMOVE_UNAVAILABLE_STATUS) {
        setAvailableFnmaStatuses([FM_STATUSES.NO_FANNIE_MAE_REVIEW, FM_STATUSES.APPROVED_BY_FANNIE_MAE]);
        setNewFNMAStatus(FM_STATUSES.NO_FANNIE_MAE_REVIEW);
      } else if (
        (request.requestTypeName === REQUEST_TYPES.ADD_UNAVAILABLE_STATUS || request.requestTypeName === REQUEST_TYPES.MODIFY_UNAVAILABLE_STATUS)
        && projectInfo.fnmaReviewStatus === FM_STATUSES.APPROVED_BY_FANNIE_MAE
      ) {
        setAvailableFnmaStatuses([FM_STATUSES.NO_FANNIE_MAE_REVIEW, FM_STATUSES.UNAVAILABLE]);
        setNewFNMAStatus(FM_STATUSES.UNAVAILABLE);
      } else if (request.requestTypeName === REQUEST_TYPES.ADD_UNAVAILABLE_STATUS || request.requestTypeName === REQUEST_TYPES.MODIFY_UNAVAILABLE_STATUS) {
        setAvailableFnmaStatuses([FM_STATUSES.UNAVAILABLE]);
        setNewFNMAStatus(FM_STATUSES.UNAVAILABLE);
      }

      // Set loading state at the start for ALL requests
      setIsLoadingStatusChangeData(true);

      (async () => {
        const allReasonCodes = await getReasonCodes();

        if (!allReasonCodes || !Array.isArray(allReasonCodes)) {
          console.error("Failed to fetch reason codes or received invalid data");
          setAllReasonCodes([]);
          setReasonCodeOptions([]);
          setIsLoadingStatusChangeData(false);
          return;
        }

        setAllReasonCodes(allReasonCodes);
        
        const reasonCodeOptions = allReasonCodes.map(rc => ({
          label: rc.reasonCode,
          value: rc.reasonId
        }));

        setReasonCodeOptions(reasonCodeOptions);

        // Fetch reason codes from the specific endpoint for Add/Remove Unavailable Status requests
        if (
          request.requestTypeName === REQUEST_TYPES.ADD_UNAVAILABLE_STATUS
          || request.requestTypeName === REQUEST_TYPES.MODIFY_UNAVAILABLE_STATUS
          || request.requestTypeName === REQUEST_TYPES.REMOVE_UNAVAILABLE_STATUS
          || request.requestTypeName === REQUEST_TYPES.REMOVE_DELIVERY_RESTRICTION
        ) {
          // For Modify Unavailable, also fetch active reason codes from FM Activity Feed
          if (request.requestTypeName === REQUEST_TYPES.MODIFY_UNAVAILABLE_STATUS && projectInfo?.projectId) {
            try {
              const { data: activityData } = await axios.get(
                `${global.REACT_APP_API_ENDPOINT}/activityFeed/getFnmaReviewStatusActivityFeedForProject/${projectInfo.projectId}`
              );
              const fmActivity = activityData?.activityFeedFnmaStatusDTO || [];
              const latestEntry = fmActivity[0];
              
              if (latestEntry?.reasonIds && latestEntry?.fnmaReviewStatus === 'Unavailable') {
                const activeCodes = latestEntry.reasonIds
                  .map(id => allReasonCodes.find(code => code.reasonId === id))
                  .filter(Boolean)
                  .sort((a, b) => a.reasonCode.localeCompare(b.reasonCode));
                setActiveReasonCodes(activeCodes);
              }
            } catch (err) {
              console.error("Error fetching active reason codes from FM Activity Feed:", err);
            }
          }

          try {
            const { data } = await axios.get(
              `${global.REACT_APP_API_ENDPOINT}/lender/request/fetch-status-change-requests?requestId=${request.requestId}`
            );

            if (request.requestTypeName === REQUEST_TYPES.REMOVE_DELIVERY_RESTRICTION) {
              const requestedTrackers = Array.isArray(data?.requestRemoveDeliveryRestrictionTrackers)
                ? data.requestRemoveDeliveryRestrictionTrackers
                : [];
              const requestedIds = Array.isArray(data?.requestRemoveDeliveryRestrictionTrackers)
                ? data.requestRemoveDeliveryRestrictionTrackers
                    .map(item => item?.restrcnId)
                    .filter(isNumeric)
                : [];
              const activeIds = Array.isArray(data?.activeDeliveryRestrictionIds)
                ? data.activeDeliveryRestrictionIds.filter(isNumeric)
                : [];

              setFetchedRequestedRestrictionIds(requestedIds);
              setFetchedActiveRestrictionIds(activeIds);
              setFetchedRemoveDRTrackers(requestedTrackers);
            }
            
            // Delta review model gate (R4-R8): only for Modify Unavailable Status, and only when the
            // backend has populated activeReasonCodes on this response. Falls back to legacy behavior
            // (byte-for-byte) when absent/empty.
            const useDeltaModel = request.requestTypeName === REQUEST_TYPES.MODIFY_UNAVAILABLE_STATUS
              && Array.isArray(data?.activeReasonCodes)
              && data.activeReasonCodes.length > 0;

            setUseReasonCodeDeltaModel(useDeltaModel);
            setDeltaActiveReasonCodeIds(useDeltaModel ? data.activeReasonCodes : []);
            // Retain lenderRequestChange here \u2014 the reasonCodeIds Set-derivation below drops it.
            setDeltaReasonCodeChanges(useDeltaModel ? (data.requestStatusChangeReasonCodes || []) : []);

            if (data && data.requestStatusChangeReasonCodes) {
              const fetchedReasonCodeIds = data.requestStatusChangeReasonCodes
                .filter(rc => rc.status !== "Modify")
                .map(rc => rc.reasonId);  

              // Store the reason code IDs in a Set for efficient lookups
              setReasonCodeIds(new Set(fetchedReasonCodeIds));

              // Filter allReasonCodes to get the full reason code objects matching the IDs
              const reasonCodeIdLookup = new Map(allReasonCodes.map(rc => [rc.reasonId, rc]));
              const selectedReasonCodes = fetchedReasonCodeIds.map(id => reasonCodeIdLookup.get(id)).filter(Boolean);

              setAddedReasonCodes(selectedReasonCodes);
            }
            
            // Store hasPersDecision from the response
            if (data && data.hasPersDecision !== undefined) {
              setHasPersDecision(data.hasPersDecision);
            }
          } catch (err) {
            console.error("Error fetching reason codes for status change request:", err);
          } finally {
            // Turn off loading AFTER all state has been set
            setIsLoadingStatusChangeData(false);
          }
        } else {
          // For non-status-change requests, turn off loading after reason codes are loaded
          setIsLoadingStatusChangeData(false);
        }
      })();
    }
  }, [show, request, projectInfo]);

  useEffect(() => {
    if (show && request && statusName && availableFnmaStatuses) {
      const showFnmaStatusDropdown = 
        canReview 
        && (
          statusName === REQUEST_STATUSES.UNDER_REVIEW 
          || statusName === REQUEST_STATUSES.ACTION_TAKEN 
        ) 
        && availableFnmaStatuses.length > 1;

      setShowFnmaStatusDropdown(showFnmaStatusDropdown);
    }
  }, [show, request, canReview, statusName, availableFnmaStatuses]);

  useEffect(() => {
    if (show && request && statusName && availableFnmaStatuses) {
      const shouldShowReasonCodes = canReview && (
        (statusName === REQUEST_STATUSES.NO_ACTION_TAKEN && (
          request.requestTypeName === REQUEST_TYPES.ADD_UNAVAILABLE_STATUS ||
          request.requestTypeName === REQUEST_TYPES.MODIFY_UNAVAILABLE_STATUS ||
          request.requestTypeName === REQUEST_TYPES.REMOVE_UNAVAILABLE_STATUS
        )) ||
        (statusName === REQUEST_STATUSES.ACTION_TAKEN && (
          request.requestTypeName === REQUEST_TYPES.ADD_UNAVAILABLE_STATUS ||
          request.requestTypeName === REQUEST_TYPES.MODIFY_UNAVAILABLE_STATUS
          // Remove Unavailable Status is NOT editable for ACTION_TAKEN
        ))
      );
      setShouldShowReasonCodes(shouldShowReasonCodes);
    }
  }, [show, request, canReview, statusName, availableFnmaStatuses]);

  useEffect(() => {    
    // Use projectRestrictions passed from parent (same data shown on Project page in Delivery Restrictions box)
    if (projectRestrictions && Array.isArray(projectRestrictions) && projectRestrictions.length > 0) {
      // console.log("[RequestDetailsModal] Using projectRestrictions from parent:", projectRestrictions);
      const sortedRestrictions = [...projectRestrictions].sort(restrictionComparer);
      setRestrictions(sortedRestrictions);
    } else if (projectInfo && restrictionsByProjectId[projectInfo.projectId]) {
      // Fallback to Redux if projectRestrictions not provided
      // console.log("[RequestDetailsModal] Loaded restrictions for project from Redux:", projectInfo.projectId, restrictionsByProjectId[projectInfo.projectId]);
      const cannedRestrictions = restrictionsByProjectId[projectInfo.projectId].preCannedDR || [];
      cannedRestrictions.sort(restrictionComparer);
      setRestrictions(cannedRestrictions);
    } else {
      // console.log('[RequestDetailsModal] No restrictions available from either source');
    }
  }, [projectRestrictions, restrictionsByProjectId, projectInfo]);

  const getReasonCodes = async () => {
    try {
      const { data } = await axios.get(global.REACT_APP_API_ENDPOINT + `/reasonCodes/getAllReasonCodes`);

      if (data && Array.isArray(data)) {
        const reasonCodes = data.sort((a, b) => a.reasonCode.localeCompare(b.reasonCode));
        return reasonCodes;
      }
      
      console.error("Invalid reason codes data received:", data);
      return [];
    } catch (err) {
      console.error("Error fetching reason codes:", err);
      return [];
    }
  };

  // Update address fields in stagingChanges from a Google Autocomplete selection.
  // fieldUpdates is an object like { street, city, state, zip5 } — falsy values are skipped.
  // Only updates fields that already exist in stagingChanges (i.e. the lender requested them).
  function handleAddressFieldsUpdate(fieldUpdates) {
    const entries = Object.entries(fieldUpdates).filter(([, v]) => v);
    if (!entries.length) return;

    setStagingChanges(prev => {
      const next = [...(prev || [])];
      entries.forEach(([fieldName, newValue]) => {
        const index = next.findIndex(fc => fc.fieldName === fieldName);
        if (index !== -1) {
          next[index] = { ...next[index], newValue };
        }
      });
      return next;
    });

    setValidationErrors(prev => {
      const next = new Map(prev);
      entries.forEach(([fieldName]) => next.delete(fieldName));
      return next;
    });

    setLineItemApprovals(prev => {
      const next = new Map(prev);
      entries.forEach(([fieldName]) => next.set(fieldName, LINE_ITEM_STATUS.MODIFY));
      return next;
    });

    setEditingItems(prev => {
      const next = new Set(prev);
      entries.forEach(([fieldName]) => next.add(fieldName));
      return next;
    });
  }

  const handleFNMAStatusChange = (status) => {
    setNewFNMAStatus(status);
    
    // Clear expiration date when changing to any status except "No Fannie Mae Review" with hasPersDecision
    if (!(hasPersDecision && status === FM_STATUSES.NO_FANNIE_MAE_REVIEW)) {
      setApprovalExpiryDate(null);
    }
  };

  const getDefaultFNMAStatus = () => {
    const isApprovedAddOrModifyUnavailable =
      (request.requestTypeName === REQUEST_TYPES.ADD_UNAVAILABLE_STATUS || request.requestTypeName === REQUEST_TYPES.MODIFY_UNAVAILABLE_STATUS)
      && projectInfo?.fnmaReviewStatus === FM_STATUSES.APPROVED_BY_FANNIE_MAE;

    if (isApprovedAddOrModifyUnavailable) {
      return FM_STATUSES.UNAVAILABLE;
    }

    if (availableFnmaStatuses.includes(FM_STATUSES.NO_FANNIE_MAE_REVIEW)) {
      return FM_STATUSES.NO_FANNIE_MAE_REVIEW;
    }

    return availableFnmaStatuses[0] || "";
  };

  useEffect(() => {
    const canAddRestrictions = canReview && statusName === REQUEST_STATUSES.ACTION_TAKEN && (
      // request.requestTypeName === "Remove Approved By Fannie Mae Status" ||
      newFNMAStatus === FM_STATUSES.APPROVED_BY_FANNIE_MAE ||
      newFNMAStatus === FM_STATUSES.CONDITIONAL_APPROVAL
    ) && newFNMAStatus !== FM_STATUSES.UNAVAILABLE;

    // const canAddConditions = canReview && internalStatus === REQUEST_STATUSES.ACTION_TAKEN && 
    //   newFNMAStatus === "Conditional Approval";

    // Reason codes are editable for eligible UV request types when Action Taken (FM status guard)
    // or when No Action Taken (no FM status gate — DEC-014).
    // Eligibility matrix:
    //   Add/Modify UV + ACTION_TAKEN (FM Unavailable) → editable
    //   Add/Modify UV + NO_ACTION_TAKEN               → editable
    //   Remove UV   + NO_ACTION_TAKEN                 → editable
    //   Remove UV   + ACTION_TAKEN                    → NOT editable
    //   Partial Action Taken (any UV type)            → NOT editable
    const canAddReasonCodes = canReview && (
      (statusName === REQUEST_STATUSES.NO_ACTION_TAKEN && (
        request.requestTypeName === REQUEST_TYPES.ADD_UNAVAILABLE_STATUS ||
        request.requestTypeName === REQUEST_TYPES.MODIFY_UNAVAILABLE_STATUS ||
        request.requestTypeName === REQUEST_TYPES.REMOVE_UNAVAILABLE_STATUS
      )) ||
      (statusName === REQUEST_STATUSES.ACTION_TAKEN && newFNMAStatus === FM_STATUSES.UNAVAILABLE && (
        request.requestTypeName === REQUEST_TYPES.ADD_UNAVAILABLE_STATUS ||
        request.requestTypeName === REQUEST_TYPES.MODIFY_UNAVAILABLE_STATUS
        // Remove Unavailable Status is NOT editable for ACTION_TAKEN
      ))
    );

    setCanAddRestrictions(canAddRestrictions);
    // setCanAddConditions(canAddConditions);
    setCanAddReasonCodes(canAddReasonCodes);

    const canReviewRestrictions = canReview && 
      request.requestTypeName === REQUEST_TYPES.REMOVE_DELIVERY_RESTRICTION &&
      (statusName === REQUEST_STATUSES.ACTION_TAKEN || statusName === REQUEST_STATUSES.NO_ACTION_TAKEN);

    setCanReviewRestrictions(canReviewRestrictions);

    // Set expiration date to current date + 45 days when hasPersDecision and status is "No Fannie Mae Review"
    if (hasPersDecision && newFNMAStatus === FM_STATUSES.NO_FANNIE_MAE_REVIEW) {
      const expirationDate = moment().add(45, 'days').format('YYYY-MM-DD');
      setApprovalExpiryDate(expirationDate);
    } else {
      // Clear expiration date when changing to any other status (including "Approved By Fannie Mae" and "Unavailable")
      setApprovalExpiryDate(null);
    }

    if (statusName !== REQUEST_STATUSES.ACTION_TAKEN) {
      setAddedRestrictions([]);
      // FM status reset: UV + No Action Taken always gets No Fannie Mae Review because the
      // request to set the project Unavailable is being declined — Unavailable in the payload
      // would be semantically wrong (M-4 / DEC-014). For other status transitions, use
      // No Fannie Mae Review when available, else fall back to the type default.
      const isUVNoActionTaken = statusName === REQUEST_STATUSES.NO_ACTION_TAKEN && (
        request.requestTypeName === REQUEST_TYPES.ADD_UNAVAILABLE_STATUS ||
        request.requestTypeName === REQUEST_TYPES.MODIFY_UNAVAILABLE_STATUS ||
        request.requestTypeName === REQUEST_TYPES.REMOVE_UNAVAILABLE_STATUS
      );
      if (isUVNoActionTaken || availableFnmaStatuses.includes(FM_STATUSES.NO_FANNIE_MAE_REVIEW)) {
        setNewFNMAStatus(FM_STATUSES.NO_FANNIE_MAE_REVIEW);
      } else {
        setNewFNMAStatus(getDefaultFNMAStatus());
      }
      // Reason code reset is guarded: preserve codes when UV editing is active for the new status (DEC-014).
      // Mirrors the eligibility matrix in shouldShowReasonCodes and canAddReasonCodes above.
      const isUVEditable = (
        (statusName === REQUEST_STATUSES.NO_ACTION_TAKEN && (
          request.requestTypeName === REQUEST_TYPES.ADD_UNAVAILABLE_STATUS ||
          request.requestTypeName === REQUEST_TYPES.MODIFY_UNAVAILABLE_STATUS ||
          request.requestTypeName === REQUEST_TYPES.REMOVE_UNAVAILABLE_STATUS
        )) ||
        (statusName === REQUEST_STATUSES.ACTION_TAKEN && (
          request.requestTypeName === REQUEST_TYPES.ADD_UNAVAILABLE_STATUS ||
          request.requestTypeName === REQUEST_TYPES.MODIFY_UNAVAILABLE_STATUS
        ))
      );
      if (!isUVEditable) {
        const originalReasonCodes = allReasonCodes.filter(rc => reasonCodeIds.has(rc.reasonId));
        setAddedReasonCodes(originalReasonCodes);
      }
    }
  // allReasonCodes and reasonCodeIds included so the reset branch reads current values and not
  // a stale closure from before the async fetch-status-change-requests call resolved.
  // availableFnmaStatuses included so the FM status reset reads the correct available statuses.
  }, [canReview, statusName, newFNMAStatus, hasPersDecision, allReasonCodes, reasonCodeIds, availableFnmaStatuses]);

  useEffect(() => {
    if (isClosedStatus(statusName) || statusName === REQUEST_STATUSES.ADDITIONAL_INFORMATION_REQUIRED) {
      setNotesExternal(true);
    } else {
      setNotesExternal(false);
    }
  }, [statusName]);

  useEffect(() => {
    if (show && request) {
      setFetchedRequestedRestrictionIds(null);
      setFetchedActiveRestrictionIds(null);
      setFetchedRemoveDRTrackers([]);
      setRequestedDocuments([]);
      setLineItemApprovals(new Map());
      setEditingItems(new Set());
      setValidationErrors(new Map());
      setPhaseEditingItems(new Map());
      setPhaseValidationErrors(new Map());
      setPhaseLineItemApprovals(new Map());
      setNote("");  
      setBypassDuplicateProjectId(null);
      setBypassDuplicateMessage(null);
      setAutocompleteFieldUpdates({});
      setAccordionExpanded(false);

      setEditedProjectProfile(null);
      setFetchedProjectProfile(null);
      setProjectProfileValid(true);
      setProjectProfileTouched(false);
      setProjectProfileHasChanges(false);

      setStatusName(request.statusName);
      setInternalStatusName(request.internalStatus);
      baselineStatusRef.current = request.statusName;

      const passData = JSON.parse(sessionStorage.getItem("passData"));

      setCanReview(
        (isProjectStandardRole() || isAdminRole()) && (
          request.assignedToLdapId?.toLowerCase() === passData?.user.toLowerCase() ||
          getEnvName() === "local"
        ) && isReviewableStatus(request.statusName)
      );

      const documents = request.requestFeedDocumentDTOList;
      setDocuments(documents);      

      switch (request.requestTypeName) {
        case REQUEST_TYPES.LENDER_EDIT_PHASE:
        case REQUEST_TYPES.LENDER_EDIT_PROJECT:
        case REQUEST_TYPES.LENDER_ADD_PROJECT:
        case REQUEST_TYPES.LENDER_ADD_PHASE:
          // Skip staging data fetch for deleted projects — the backend returns 500 for
          // staging records on a deleted project, which triggers the global error redirect.
          if (isProjectDeleted) break;
          (async () => {
            try {
            const {originalChanges, addedPhases, bypassDuplicateProjectId, bypassDuplicateMessage} = await getStagingChanges(request.requestId);

            if (request.requestTypeName === REQUEST_TYPES.LENDER_ADD_PROJECT) {
              setBypassDuplicateProjectId(bypassDuplicateProjectId || null);
              setBypassDuplicateMessage(bypassDuplicateMessage || null);
            }

            if (originalChanges) {
              setOriginalChanges(originalChanges);
              setStagingChanges(originalChanges.map(change => ({...change})));
            } else if (
              request.requestTypeName === REQUEST_TYPES.LENDER_EDIT_PHASE || 
              request.requestTypeName === REQUEST_TYPES.LENDER_EDIT_PROJECT
            ) {
              console.error("Failed to load staging changes for request", request, originalChanges);
            }

            if (addedPhases) {
              console.info("✓ Successfully loaded phases from API", {
                phaseCount: addedPhases.length,
                phases: addedPhases,
                requestId: request.requestId,
                requestType: request.requestTypeName,
              });
              setOriginalAddedPhases(addedPhases);
              setAddedPhases(addedPhases.map(phase => ({...phase})));
            } else if (
              request.requestTypeName === REQUEST_TYPES.LENDER_ADD_PROJECT || 
              request.requestTypeName === REQUEST_TYPES.LENDER_ADD_PHASE
            ) {
              console.error("Failed to load phase list for request", request, addedPhases);
            }

            if (request.requestTypeName === REQUEST_TYPES.LENDER_ADD_PROJECT) {
              const projectId = request.projectId || projectInfo?.projectId || 641914;

              if (projectId) {
                try {
                  const projectDetailsResponse = await axios.get(
                    `${global.REACT_APP_API_ENDPOINT}/condoMgr/getProjectDetailsByProjectId/${projectId}`
                  );
                  setFetchedProjectProfile(projectDetailsResponse?.data?.projectProfile || null);
                } catch (error) {
                  console.error("Failed to fetch project details to pre-populate Project Info", error);
                }
              }
            }
            } catch (err) {
              console.error("Failed to load staging data for request — project may be deleted or unavailable", err);
            }
          })();
          break;
      }
    }
  }, [show, request, projectFieldsMap, phaseFieldsMap, isProjectDeleted]);

  useEffect(() => {
    if (show && request && request.statusName === REQUEST_STATUSES.ADDITIONAL_INFORMATION_REQUIRED) {
      (async function() {
        const docRequestEvent = request.requestFeedActivityTrackerDTOList?.findLast(evt => 
          evt.externalStatus?.toLowerCase() === REQUEST_STATUSES.ADDITIONAL_INFORMATION_REQUIRED.toLowerCase() &&
          evt.activity?.toLowerCase() === "status changed"
        );
        
        if (docRequestEvent) {
          const requestActivityTrackerId = docRequestEvent.requestActivityTrackerId;
          const additionalDocuments = await getAdditionalDocuments(requestActivityTrackerId);
          setAdditionalDocuments(additionalDocuments);
        }
      })();
    }
  }, [show, request]);

  // Diagnostic: log "Requested Phases" render condition state
  useEffect(() => {
    if (show && request && (request.requestTypeName === REQUEST_TYPES.LENDER_ADD_PROJECT || request.requestTypeName === REQUEST_TYPES.LENDER_ADD_PHASE)) {
      const shouldShowPhases = addedPhases?.length && (
        request.requestTypeName !== REQUEST_TYPES.LENDER_ADD_PROJECT || 
        !(isReadOnlyMode && currentProjectType === PROJECT_TYPES.ESTABLISHED_CONDO)
      );
      
      console.info("Requested Phases render condition state:", {
        visible: !!shouldShowPhases,
        addedPhases: addedPhases,
        addedPhasesLength: addedPhases?.length,
        requestTypeName: request.requestTypeName,
        isReadOnlyMode,
        currentProjectType,
        statusName,
        canReview,
        conditions: {
          'has addedPhases': !!addedPhases?.length,
          'not LENDER_ADD_PROJECT': request.requestTypeName !== REQUEST_TYPES.LENDER_ADD_PROJECT,
          'not (read-only AND established)': !(isReadOnlyMode && currentProjectType === PROJECT_TYPES.ESTABLISHED_CONDO),
        },
      });
    } else if (show && request) {
      // Log why phases section isn't showing
      console.info("Requested Phases section not applicable for this request:", {
        requestTypeName: request.requestTypeName,
        isLenderAddProject: request.requestTypeName === REQUEST_TYPES.LENDER_ADD_PROJECT,
        isLenderAddPhase: request.requestTypeName === REQUEST_TYPES.LENDER_ADD_PHASE,
        hasAddedPhases: !!addedPhases?.length,
        addedPhasesCount: addedPhases?.length || 0,
      });
    }
  }, [show, request, addedPhases, isReadOnlyMode, currentProjectType, statusName, canReview]);

  const isModified = fieldName => {
    const requestedChange = originalChanges.find(fc => fc.fieldName === fieldName);
    const modifiedChange = stagingChanges.find(fc => fc.fieldName === fieldName);
    return !!requestedChange && (requestedChange.newValue !== modifiedChange?.newValue);
  };

  // Stable ID arrays for DeliveryRestrictionsReviewSection — avoids new array references on every render
  const currentRestrictionIds = useMemo(() => {
    if (Array.isArray(fetchedActiveRestrictionIds) && fetchedActiveRestrictionIds.length > 0) {
      return fetchedActiveRestrictionIds;
    }

    return currentRestrictions?.map(r => r.restrictionId) || [];
  }, [currentRestrictions, fetchedActiveRestrictionIds]);
  const requestedRestrictionIds = useMemo(
    () => Array.isArray(fetchedRequestedRestrictionIds)
      ? fetchedRequestedRestrictionIds
      : (request.removeDrDetails?.map(r => r.restrcnId) || []),
    [fetchedRequestedRestrictionIds, request.removeDrDetails]
  );
  const isRemoveDRDecisionStatus = statusName === REQUEST_STATUSES.ACTION_TAKEN
    || statusName === REQUEST_STATUSES.NO_ACTION_TAKEN;
  const showRemoveDRFinalRestrictions = isRemoveDRDecisionStatus;

  // Derived flag: true when any requested document is "Other" (case-insensitive)
  const isOtherRequested = useMemo(
    () => requestedDocuments?.some(d => d.name?.toLowerCase() === "other"),
    [requestedDocuments]
  );

  // VALIDATION LOGIC FOR SUBMIT BUTTON
  // All logic for enabling/disabling the submit button is contained in this useEffect. 
  useEffect(() => {
    if (canReview && statusName && request) {
      // Shorthand variables to make the code more readable
      const actionTaken = statusName === REQUEST_STATUSES.ACTION_TAKEN;
      const noActionTaken = statusName === REQUEST_STATUSES.NO_ACTION_TAKEN;
      const partialActionTaken = statusName === REQUEST_STATUSES.PARTIAL_ACTION_TAKEN;
      const infoRequired = statusName === REQUEST_STATUSES.ADDITIONAL_INFORMATION_REQUIRED;
      const inProgressFromUnderReviewBaseline =
        statusName === REQUEST_STATUSES.IN_PROGRESS &&
        baselineStatusRef.current === REQUEST_STATUSES.UNDER_REVIEW;

      // Check if all line items are reviewed
      const allItemsReviewed = stagingChanges?.every(fieldChange => 
        lineItemApprovals?.has(fieldChange.fieldName)
        && (
          // If a line item is marked as "Modify", the value must be different
          lineItemApprovals?.get(fieldChange.fieldName) !== LINE_ITEM_STATUS.MODIFY 
          || isModified(fieldChange.fieldName)
        )
      );

      // Check if all phase items are reviewed, if any
      const allPhaseItemsReviewed = !addedPhases?.length || addedPhases.every(phase => phase.status);   
    
      // Check if there are any phase validation errors
      const hasPhaseValidationErrors = Array.from(phaseValidationErrors.values()).some(phaseErrors => 
        phaseErrors.size > 0
      );

      // Is this a Status Review request type?
      const isStatusReviewRequest = 
        request.requestTypeName === REQUEST_TYPES.REMOVE_UNAVAILABLE_STATUS || 
        request.requestTypeName === REQUEST_TYPES.ADD_UNAVAILABLE_STATUS || 
        request.requestTypeName === REQUEST_TYPES.MODIFY_UNAVAILABLE_STATUS;
      
      // Check for required questions
      const isExpiryDateRequired = isStatusReviewRequest && actionTaken && newFNMAStatus === FM_STATUSES.APPROVED_BY_FANNIE_MAE;
      const isSuspensionReasonRequired = isStatusReviewRequest && actionTaken && hasPersDecision && newFNMAStatus === FM_STATUSES.NO_FANNIE_MAE_REVIEW;
      const areIneligibilityFieldsRequired = isStatusReviewRequest && actionTaken && hasPersDecision && newFNMAStatus === FM_STATUSES.UNAVAILABLE;

      // Check potentially required questions to see if they're empty
      const isExpiryDateEmpty = !approvalExpiryDate;
      const isSuspensionReasonEmpty = !suspensionReason.trim();
      const areIneligibilityFieldsEmpty = (
        !sellingServicingGuideRef.trim() || 
        !reviewedProjectExhibits.trim() || 
        !reasonForIneligibility.trim()
      );
      
      // If "Other" doc type is requested, notes are mandatory
      const isNoteMissing = isOtherRequested && !note.trim();

      // Reviewer can only submit the form if:
      // 1. The requestStatuses list has been loaded (ensures statusId will resolve on submit)
      // 2. There are no validation errors in the main form
      // 3. There are no validation errors in any phase, if applicable
      // 4. If the reason codes are editable, at least one must be selected
      // 5. If there are any required questions based on the new FM status, they can't be empty
      // 6. If "Other" doc type is requested, notes must be non-empty
      // 7. The request must have a review decision (ie accept, reject, partial accept, or additional info required)
      //    - If the status decision is Additional Information Required, then at least one document must be requested
      //    - If the status decision is Action Taken, then all phase items must be reviewed, if any
      //    - If the status decision is Partial Action Taken, then all line items and all phase items must be reviewed
      //    - If the status decision is No Action Taken, no line item review is required
      setCanSubmit(
        !!requestStatuses?.length &&
        !validationErrors.size && 
        !hasPhaseValidationErrors && 
        projectProfileValid &&
        // Reason codes are required for both Action Taken and No Action Taken UV decisions
        // (must choose at least one reason code either way).
        (!canAddReasonCodes || addedReasonCodes?.length) &&
        (!isExpiryDateRequired || !isExpiryDateEmpty) &&
        (!isSuspensionReasonRequired || !isSuspensionReasonEmpty) &&
        (!areIneligibilityFieldsRequired || !areIneligibilityFieldsEmpty) &&
        !isNoteMissing &&
        (
          (statusName === REQUEST_STATUSES.IN_PROGRESS && !inProgressFromUnderReviewBaseline) ||
          (infoRequired && requestedDocuments?.length) ||
          (actionTaken && allPhaseItemsReviewed) || 
          (partialActionTaken && allItemsReviewed && allPhaseItemsReviewed) ||
          noActionTaken
        )
      );
    }
  }, [request, statusName, lineItemApprovals, phaseLineItemApprovals, stagingChanges, canReview, requestedDocuments, validationErrors, phaseValidationErrors, addedPhases, addedReasonCodes, newFNMAStatus, approvalExpiryDate, hasPersDecision, suspensionReason, sellingServicingGuideRef, reviewedProjectExhibits, reasonForIneligibility, canAddReasonCodes, note, projectProfileValid, requestStatuses]);
       
  async function getStagingChanges(requestId) {
    try {
      const response = await axios.get(
        `${global.REACT_APP_API_ENDPOINT}/lender/request/projectStagingData?masterTrackerId=${requestId}`,
        { skipGlobalErrorRedirect: true }
      );
      
      if (response.status === 200) {
        let originalChanges = [];

        if (response.data?.stagingDetails?.length) {
          originalChanges = response.data.stagingDetails.map(change => {
            const resolvedFieldDef = request.requestTypeName === REQUEST_TYPES.LENDER_EDIT_PROJECT 
              ? projectFieldsMap.get(change.dataAttributeName)
              : phaseFieldsMap.get(change.dataAttributeName);
            const fieldDef = resolvedFieldDef || createFallbackFieldDef(change.dataAttributeName);

            if (!resolvedFieldDef) {
              console.warn("Missing field definition for requested change", {
                fieldName: change.dataAttributeName,
                requestId,
                requestType: request.requestTypeName,
              });
            }
    
            return {
              fieldName: change.dataAttributeName,
              oldValue: parseBoolean(change.oldValue),
              newValue: parseBoolean(change.newValue),
              def: fieldDef,
              label: fieldDef?.formatLabel?.() || change.dataAttributeName,
              formatValue: fieldDef?.formatValue || ((value) => value || "(none)"),
            };
          });  
        }

        let addedPhases = null;

        if (response.data?.condoPhaseStagingDetails?.length) {
          addedPhases = response.data.condoPhaseStagingDetails.map(phaseDto => {
            return {
              phaseId: phaseDto.phaseId,
              phaseName: phaseDto.phaseName,
              street: phaseDto.street,
              city: phaseDto.city,
              state: phaseDto.state,
              zip5: phaseDto.zip5,
              buildingNumber: phaseDto.bldgNbr,
              unitNumber: phaseDto.unitNbr,
              unitCount: phaseDto.phaseUnitCount,
              // Preserve the request status from API if present (will be overridden by cascade effect based on project type)
              status: phaseDto.requestStatus || undefined,
            };
          });
        } else {
          // Log when phases are empty or missing
          console.info("No phases in projectStagingData response", {
            requestId,
            requestType: request.requestTypeName,
            responseKeys: Object.keys(response.data || {}),
            condoPhaseStagingDetailsValue: response.data?.condoPhaseStagingDetails,
            hasCondoPhaseStagingDetailsKey: 'condoPhaseStagingDetails' in (response.data || {}),
          });
        }

        if (request.requestTypeName === REQUEST_TYPES.LENDER_EDIT_PHASE) {
          setPhaseName(response.data?.phaseName);
          setPhaseId(response.data?.phaseId);
        }

        return {originalChanges, addedPhases, bypassDuplicateProjectId: response.data?.bypassDuplicateProjectId, bypassDuplicateMessage: response.data?.bypassDuplicate};
      }

      console.error("Error fetching requested edits: unexpected status code", {
        status: response.status,
        statusText: response.statusText,
        data: response.data,
        requestId,
        url: `${global.REACT_APP_API_ENDPOINT}/lender/request/projectStagingData?masterTrackerId=${requestId}`,
      });
      return {originalChanges: [], addedPhases: null};
    } catch (error) {
      console.error("Error fetching requested edits - network or parsing error:", {
        message: error.message,
        code: error.code,
        response: error.response?.status,
        requestId,
        url: `${global.REACT_APP_API_ENDPOINT}/lender/request/projectStagingData?masterTrackerId=${requestId}`,
        error,
      });
      return {originalChanges: [], addedPhases: null};
    }
  }

  const handleClose = () => {
    setEditedProjectProfile(null);
    setFetchedProjectProfile(null);
    setProjectProfileValid(true);
    setProjectProfileTouched(false);
    setProjectProfileHasChanges(false);
    onClose();
  }

  const handleProjectProfileChanged = (editedDao, isValid, touched) => {
    const originalProfile = fetchedProjectProfile || projectInfo?.projectProfile || projectInfo || {};
    const hasChanges = Object.keys(editedDao).some(
      key => editedDao[key] !== originalProfile[key]
    );
    setProjectProfileTouched(touched);
    setProjectProfileValid(isValid);
    setProjectProfileHasChanges(hasChanges);
    setEditedProjectProfile(hasChanges ? editedDao : null);
  };

  const handleAddReasonCodes = (values) => {
    // react-select passes null (not []) when the last tag is cleared via the multi-select's clear-all control
    const selectedReasonCodes = allReasonCodes.filter(rc => 
      (values || []).find(v => v.value === rc.reasonId)
    );
    setAddedReasonCodes(selectedReasonCodes);
  };

  const handleRemoveDocType = (docTypeKey) => {
    setRequestedDocuments(prevDocTypes => prevDocTypes.filter(dt => dt.documentRankingId !== docTypeKey));
  }

  const handleAddDocType = (docTypeKey) => {
    // Use availableDocuments if we're in "Additional Information Required" mode, otherwise use documents
    const sourceDocuments = statusName === REQUEST_STATUSES.ADDITIONAL_INFORMATION_REQUIRED 
      ? availableDocuments 
      : documents;
    
    const docType = sourceDocuments.find(dt => dt.documentRankingId.toString() === docTypeKey);

    if (docType && !requestedDocuments.find(dt => dt.documentRankingId === docType.documentRankingId)) {
      const normalizedDocType = {
        ...docType,
        documentName: docType.documentName ?? docType.name
      };
      setRequestedDocuments(prevDocTypes => [...prevDocTypes, normalizedDocType]);
    }
  }

  const handleRemoveRestriction = (restrictionKey) => {
    setAddedRestrictions(prevRestrictions => prevRestrictions.filter(r => {
      const key = (r.restrictionId != null) ? String(r.restrictionId) : (r.restrictionName ?? r.restrictionsName);
      return key !== restrictionKey;
    }));
  }

  const handleAddRestriction = (restrictionKey) => {
    const restriction = restrictions.find(r => {
      const key = (r.restrictionId != null) ? String(r.restrictionId) : (r.restrictionName ?? r.restrictionsName);
      return key === restrictionKey;
    });

    if (!restriction) return;

    const alreadyAdded = addedRestrictions.some(r => {
      if (r.restrictionId != null && restriction.restrictionId != null) {
        return r.restrictionId === restriction.restrictionId;
      }
      return (r.restrictionName ?? r.restrictionsName) === (restriction.restrictionName ?? restriction.restrictionsName);
    });

    if (!alreadyAdded) {
      setAddedRestrictions(prevRestrictions => [...prevRestrictions, restriction]);
    }
  }

  const handleLineItemStatus = (fieldName, lineItemStatus) => {
    setLineItemApprovals(prev => new Map(prev).set(fieldName, lineItemStatus));
    
    // If projectStyle was requested to be changed to "Other" and that change is rejected, styleComments change must be rejected
    if (fieldName === "projectStyle") {
      const projectStyleChange = stagingChanges.find(fc => fc.fieldName === "projectStyle");
      if (projectStyleChange) {
        if (projectStyleChange.newValue === "Other") {
          const styleCommentsChange = stagingChanges.find(fc => fc.fieldName === "styleComments");

          // styleComments should be added/removed automatically when projectStyle is set to/from Other
          if (!styleCommentsChange) {
            console.error("styleComments change not found when projectStyle is Other", {stagingChanges, projectStyleChange});
            return;
          }

          if (lineItemStatus === LINE_ITEM_STATUS.REJECT) {
            setLineItemApprovals(prev => new Map(prev).set("styleComments", LINE_ITEM_STATUS.REJECT));
            setEditingItems(prev => { const next = new Set(prev); next.delete("styleComments"); return next; });
          } else {
            setLineItemApprovals(prev => new Map(prev).set("styleComments", LINE_ITEM_STATUS.ACCEPT));
          }
        } else {
          // If projectStyle is changed to a value other than "Other", remove any styleComments change
          setLineItemApprovals(prev => {
            const next = new Map(prev);
            next.delete("styleComments");
            return next;
          });

          const styleCommentsIndex = stagingChanges.findIndex(fc => fc.fieldName === "styleComments");
          if (styleCommentsIndex !== -1) {
            setStagingChanges(prev => { 
              const next = [...prev]; 
              next.splice(styleCommentsIndex, 1); 
              return next; 
            });
          }
        }
      }
    }
    
    if (lineItemStatus === LINE_ITEM_STATUS.MODIFY && !editingItems.has(fieldName)) {
      setEditingItems(prev => new Set(prev).add(fieldName));
    } else if (lineItemStatus !== LINE_ITEM_STATUS.MODIFY && editingItems.has(fieldName)) {
      setEditingItems(prev => { 
        const next = new Set(prev); 
        next.delete(fieldName); 
        return next; 
      });
      undoModifyField(fieldName);
    }
  };

  const handleFieldEditing = (e, fieldName) => {
    e?.stopPropagation();
    setEditingItems(prev => new Set(prev).add(fieldName));
  };

  const cancelFieldEditing = (e, fieldName) => {
    e?.stopPropagation();
    setEditingItems(prev => { 
      const _editingItems = new Set(prev); 
      _editingItems.delete(fieldName); 
      return _editingItems; 
    });
    undoModifyField(fieldName);
  };

  const undoModifyField = (fieldName) => {
    const modified = stagingChanges.find(fc => fc.fieldName === fieldName);
    const original = originalChanges.find(fc => fc.fieldName === fieldName);

    if (modified && original) {
      // revert the field value back to the original value in stagingChanges
      setStagingChanges(prev => {
        const next = [...prev];
        const index = next.findIndex(fc => fc.fieldName === fieldName);
        if (index !== -1) {
          next[index] = { ...original };
        }
        return next;
      });

      setValidationErrors(prev => {
        const next = new Map(prev);
        next.delete(fieldName);
        return next;
      });

      // If (and only if) the field being reverted is "projectStyle", we also need to revert "styleComments"
      if (fieldName === "projectStyle") {    
        setStagingChanges(prev => {
          const next = [...prev];
          const styleCommentsChangeIndex = next.findIndex(fc => fc.fieldName === "styleComments");
          if (styleCommentsChangeIndex !== -1) {
            next.splice(styleCommentsChangeIndex, 1);
          }
          return next;
        });

        if (original.newValue === "Other") {          
          setStagingChanges(prev => {
            const next = [...prev];
            const projectStyleChangeIndex = next.findIndex(fc => fc.fieldName === "projectStyle");
            const styleCommentsChange = originalChanges.find(fc => fc.fieldName === "styleComments");
            next.splice(projectStyleChangeIndex + 1, 0, { ...styleCommentsChange });
            return next;
          });
        }

        setValidationErrors(prev => {
          const next = new Map(prev);
          next.delete("styleComments");
          return next;
        });
      }
    }
  };

  const handleValueModified = (fieldName, modifiedValue, validationMessage, changeIndex) => {
    setStagingChanges(prev => {
      const next = [...prev];
      const index = next.findIndex(fc => fc.fieldName === fieldName);
      if (index !== -1) {
        next[index] = {
          ...next[index], 
          newValue: modifiedValue
        };
      }
      return next;
    });

    setValidationErrors(prev => {
      const next = new Map(prev);
      if (validationMessage) {
        next.set(fieldName, validationMessage);
      } else {
        next.delete(fieldName);
      }
      return next;
    });

    setLineItemApprovals(prev => new Map(prev).set(fieldName, LINE_ITEM_STATUS.MODIFY));

    // If (and only if) the field being modified is "projectStyle", we also need to add or remove "styleComments"
    // depending on whether the new value is "Other".
    // - If the new value is "Other":
    //   - If it's different from the original value, add a new change for "styleComments" right after "projectStyle" change.
    //   - If it's the same as the original value, revert any existing "styleComments" change back to original value.
    // - If the new value is anything other than "Other", remove any "styleComments" change if it exists.
    if (fieldName === "projectStyle") {
      if (modifiedValue === "Other") {
        // Try to find a pre-existing styleComments change from originalChanges
        let styleCommentsChange = originalChanges.find(fc => fc.fieldName === "styleComments");

        // If no pre-existing styleComments change exists, create a new one with empty values
        if (!styleCommentsChange) {
          const styleCommentsDef = projectFieldsMap.get("styleComments");
          styleCommentsChange = {
            fieldName: "styleComments",
            newValue: "",
            oldValue: "",
            def: styleCommentsDef,
            label: styleCommentsDef?.formatLabel?.() || "Style Comments",
            formatValue: styleCommentsDef?.formatValue || ((value) => value || "(none)"),
          };
        }

        setStagingChanges(prev => {
          const next = [...prev];
          const projectStyleChangeIndex = next.findIndex(fc => fc.fieldName === "projectStyle");
          next.splice(projectStyleChangeIndex + 1, 0, styleCommentsChange);
          return next;
        });

        // put new styleComments change into editing mode and set its approval to Modify
        setEditingItems(prev => new Set(prev).add("styleComments"));

        setValidationErrors(prev => {
          const next = new Map(prev);
          next.delete("styleComments");
          return next;
        });

        // if (statusName === REQUEST_STATUSES.PARTIAL_ACTION_TAKEN) {
          setLineItemApprovals(prev => new Map(prev).set("styleComments", LINE_ITEM_STATUS.MODIFY));
        // }
      } else {
        // Remove any existing styleComments change
        setStagingChanges(prev => prev.filter(fc => fc.fieldName !== "styleComments"));

        setLineItemApprovals(prev => {
          const next = new Map(prev);
          next.delete("styleComments");
          return next;
        });
        
        setValidationErrors(prev => {
          const next = new Map(prev);
          next.delete("styleComments");
          return next;
        });
      }
    }
  }

  const handlePhaseValueModified = (phaseIndex, fieldName, modifiedValue, validationMessage) => {
    const phase = addedPhases[phaseIndex];
    const originalPhase = originalAddedPhases[phaseIndex];

    if (phase) {
      setAddedPhases(prev => {
        const next = [...prev];
        const updatedPhase = { ...next[phaseIndex], [fieldName]: modifiedValue };
        updatedPhase.status = updatedPhase[fieldName] !== originalPhase[fieldName] ? LINE_ITEM_STATUS.MODIFY : LINE_ITEM_STATUS.ACCEPT;
        next[phaseIndex] = updatedPhase;
        return next;
      });

      setPhaseValidationErrors(prev => {
        const next = new Map(prev);

        if (!next.has(phaseIndex)) {
          next.set(phaseIndex, new Map());
        }

        const phaseErrors = new Map(next.get(phaseIndex));

        if (validationMessage) {
          phaseErrors.set(fieldName, validationMessage);
        } else {
          phaseErrors.delete(fieldName);
        }

        next.set(phaseIndex, phaseErrors);
        return next;
      });

      setPhaseLineItemApprovals(prev => {
        const next = new Map(prev);
        const currentPhaseStatus = next.get(phaseIndex) || LINE_ITEM_STATUS.ACCEPT;

        if (currentPhaseStatus !== LINE_ITEM_STATUS.MODIFY) {
          const originalValue = originalPhase ? originalPhase[fieldName] : null;
          const newValue = modifiedValue;
          const newStatus = (newValue !== originalValue) ? LINE_ITEM_STATUS.MODIFY : LINE_ITEM_STATUS.ACCEPT;
          next.set(phaseIndex, newStatus);
        }

        return next;
      });
    }
  };

  const handlePhaseValidation = (phaseIndex, fieldName, validationMessage) => {
    setPhaseValidationErrors(prev => {
      const next = new Map(prev);

      if (!next.has(phaseIndex)) {
        next.set(phaseIndex, new Map());
      }

      const phaseErrors = new Map(next.get(phaseIndex));

      if (validationMessage) {
        phaseErrors.set(fieldName, validationMessage);
      } else {
        phaseErrors.delete(fieldName);
      }

      next.set(phaseIndex, phaseErrors);
      return next;
    });
  };

  const handleStatusChange = (newStatus) => {
    if (newStatus !== statusName) {
      setStatusName(newStatus);
      setRequestedDocuments([]);
      setLineItemApprovals(new Map());
      setEditingItems(new Set());
      setValidationErrors(new Map());
      setPhaseEditingItems(new Map());
      setPhaseValidationErrors(new Map());
      setPhaseLineItemApprovals(new Map());
      setStagingChanges(originalChanges?.map(change => ({...change})));

      // Populate available documents from Redux state when Additional Information Required is selected
      if (newStatus === REQUEST_STATUSES.ADDITIONAL_INFORMATION_REQUIRED) {
        if (allDocTypesById?.size) {
          const sortedDocs = Array.from(allDocTypesById.values())
            .filter(doc => doc.active)
            .map(doc => ({
              ...doc,
              documentName: doc.documentName ?? doc.name
            }))
            .sort((a, b) => {
              const aIsOther = a.name?.toLowerCase() === "other";
              const bIsOther = b.name?.toLowerCase() === "other";
              if (aIsOther && !bIsOther) return 1;
              if (!aIsOther && bIsOther) return -1;
              if (a.required && !b.required) return -1;
              if (!a.required && b.required) return 1;
              return (a.name ?? "").localeCompare(b.name ?? "", undefined, { sensitivity: "base" });
            });

          setAvailableDocuments(sortedDocs);
        } else {
          setAvailableDocuments([]);
        }
      } else {
        setAvailableDocuments([]);
      }

      if (originalAddedPhases) {        
        const addedPhases = originalAddedPhases.map(phase => ({
          ...phase,
          status: currentProjectType === PROJECT_TYPES.ESTABLISHED_CONDO
            ? LINE_ITEM_STATUS.REJECT
            : (newStatus === REQUEST_STATUSES.ACTION_TAKEN || newStatus === REQUEST_STATUSES.PARTIAL_ACTION_TAKEN)
            ? LINE_ITEM_STATUS.ACCEPT
            : newStatus === REQUEST_STATUSES.NO_ACTION_TAKEN
            ? LINE_ITEM_STATUS.REJECT
            : undefined
        }));

        setAddedPhases([...addedPhases]);
      }

      setNewFNMAStatus(getDefaultFNMAStatus());
    }
  };

  const handleSubmit = () => {
    if (isProjectDeleted) return;
    setIsSubmitting(true);
    
    // Use a short delay to give the other handlers a chance to run
    DelayFor(40).then(async () => {
      // In the backend, "Under Review" is the stored status that maps to "In Progress" in the UI
      const validateStatusName = statusName === REQUEST_STATUSES.IN_PROGRESS
        ? REQUEST_STATUSES.UNDER_REVIEW
        : statusName;
      const statusId = requestStatuses.find(
        status => status.statusName === validateStatusName)?.projectRequestStatusId;

      const passData = JSON.parse(sessionStorage.getItem("passData"));

      // Check if this is a status change request (Add/Remove Unavailable Status)
      if (
        request.requestTypeName === REQUEST_TYPES.ADD_UNAVAILABLE_STATUS
        || request.requestTypeName === REQUEST_TYPES.MODIFY_UNAVAILABLE_STATUS
        || request.requestTypeName === REQUEST_TYPES.REMOVE_UNAVAILABLE_STATUS
      ) {
        // Build reasonCodesDetails with proper status
        let reasonCodesDetails;
        if (useReasonCodeDeltaModel) {
          // Delta review model (R4-R8): decisions already computed by ReasonCodesReviewSection.
          reasonCodesDetails = reasonCodeDecisions.map(({ reasonCodeId, status, lenderRequestChange }) => ({
            reasonCodeId,
            status,
            lenderRequestChange
          }));
        } else {
          const currentReasonCodeIds = new Set(addedReasonCodes.map(rc => rc.reasonId));

          // Get all unique reason code IDs from both reference and current
          const allReasonCodeIds = new Set([...reasonCodeIds, ...currentReasonCodeIds]);

          reasonCodesDetails = Array.from(allReasonCodeIds).map(reasonCodeId => {
            const inReference = reasonCodeIds.has(reasonCodeId);
            const inCurrent = currentReasonCodeIds.has(reasonCodeId);

            let status;
            if (inCurrent && !inReference) {
              // Not present in reference but present in current = Modify (newly added)
              status = LINE_ITEM_STATUS.MODIFY;
            } else if (inCurrent && inReference) {
              // Present in both = Accept (unchanged)
              status = LINE_ITEM_STATUS.ACCEPT;
            } else if (!inCurrent && inReference) {
              // Not present in current but present in reference = Reject (removed)
              status = LINE_ITEM_STATUS.REJECT;
            }

            return {
              reasonCodeId,
              status
            };
          });
        }

        const statusChangePayload = {
          statusId,
          createdBy: passData.user,
          assignTo: request.assignedToLdapId || passData.user,
          fmReviewStatus: newFNMAStatus,
          expirationDate: approvalExpiryDate || null,
          ...(reasonCodesDetails ? { reasonCodesDetails } : {}),
          notes: note,
          notesIsExternal: notesExternal,
          documentRankingList: requestedDocuments.map(dt => dt.documentRankingId),
          deliveryRestrictions: addedRestrictions
            .map(r => r.restrictionName ?? r.restrictionsName)
            .filter(Boolean),
          suspensionReason: hasPersDecision && newFNMAStatus === FM_STATUSES.NO_FANNIE_MAE_REVIEW ? suspensionReason : null,
          persDecisionIneligible: hasPersDecision && newFNMAStatus === FM_STATUSES.UNAVAILABLE ? {
            sellingGuideReference: sellingServicingGuideRef,
            reviewedProjectExhibits: reviewedProjectExhibits,
            ineligibilityExplaination: reasonForIneligibility
          } : null
        };

        try {
          const response = await axios.post(
            `${global.REACT_APP_API_ENDPOINT}/lender/request/review-status-request/${request.requestId}/${projectInfo.projectId}`,
            statusChangePayload
          );

          if (response.status === 200) {
            onClose();
            window.location.reload();
          } else {
            console.error("Error submitting status change review:", response);
            setSnackbarMessage("An error occurred while submitting the review. Please try again.");
            setShowSnackbar(true);
          }
        } catch (error) {
          console.error("Error submitting status change review:", error);
          setSnackbarMessage("An error occurred while submitting the review. Please try again.");
          setShowSnackbar(true);
        } finally {
          setIsSubmitting(false);
        }
      } else if (request.requestTypeName === REQUEST_TYPES.REMOVE_DELIVERY_RESTRICTION) {
        // Note: Previously, restrcnDetails was a simple array of numeric IDs representing the final list of
        // delivery restrictions that should remain on the project after completing the review. This causes
        // difficulty for the API, so we have returned to a list of structs specifying Accept/Reject/Modify
        // status. We just need to compare three lists of restrictionIds that we conveniently already have: 
        // The existing restrictions on the project, the restrictions the lender asked to remove, and the 
        // final result described above.
        // We can calculate the payload easily in 2 steps:
        // 1. Check requestedRestrictionIds. Everything that was part of the original request should be
        //    marked either Accept or Remove. 
        //    - If the ID is NOT in the final list, the reviewer removed it as requested. Status is Accept.
        //    - Else, the reviewer declined to remove it. Status is Reject.
        // 2. Now compare the initial state to the end state. Ignore anything in requestedRestrictionIds,
        //    since we've already covered those. The reviewer is permitted to add or remove any restrictions
        //    they want, but any changes that weren't part of the request are always Modify status. It 
        //    doesn't matter if they were added or removed, because Modify tells the API all it needs to
        //    know:
        //    - If it was on the project before, and it's not in the final list, then the reviewer removed
        //      it. Status is Modify.
        //    - If it wasn't on the project before, and it is now, then the reviewer added it. Status is
        //      still Modify.
        //    - Since we already covered everything that was in the request, we don't care about any other
        //      restrictions that didn't change in the final list. Ignore those, we're done.

        const restrictionDecisions = new Map();

        const start = new Set(currentRestrictionIds);
        const end = new Set(deliveryRestrictionDecisions);
        const requested = new Set(requestedRestrictionIds);

        // A Declined request retains every lender-requested restriction, while
        // reviewer-added or removed restrictions still use the normal Modify status.
        requested.forEach(restrictionId => {
          const status = end.has(restrictionId)
            ? LINE_ITEM_STATUS.REJECT
            : LINE_ITEM_STATUS.ACCEPT;

          restrictionDecisions.set(restrictionId, status);
        });

        [...difference(start, end), ...difference(end, start)]
          .filter(restrictionId => !requested.has(restrictionId))
          .forEach(restrictionId => restrictionDecisions.set(restrictionId, LINE_ITEM_STATUS.MODIFY));

        // Completed and Declined use the same final-ID payload. Declined
        // excludes lender-requested Reject rows and sends each internal
        // Modify row once.
        const restrictionPayload = Array.from(restrictionDecisions.entries())
          .filter(([, status]) =>
            status === LINE_ITEM_STATUS.ACCEPT || status === LINE_ITEM_STATUS.MODIFY
          )
          .map(([restrictionId]) => restrictionId);

        const reviewPayload = {
          statusId,
          createdBy: passData.user,
          assignTo: request.assignedToLdapId || passData.user,
          notes: note,
          notesIsExternal: notesExternal,
          documentRankingList: requestedDocuments.map(dt => dt.documentRankingId),
          restrcnDetails: restrictionPayload
        };

        try {
          const response = await axios.post(
            `${global.REACT_APP_API_ENDPOINT}/lender/request/review-status-request/${request.requestId}/${projectInfo.projectId}`,
            reviewPayload
          );

          if (response.status === 200) {
            onClose();
            window.location.reload();
          } else {
            console.error("Error submitting Remove Delivery Restriction review:", response);
            setSnackbarMessage("An error occurred while submitting the review. Please try again.");
            setShowSnackbar(true);
          }
        } catch (error) {
          console.error("Error submitting Remove Delivery Restriction review:", error);
          setSnackbarMessage("An error occurred while submitting the review. Please try again.");
          setShowSnackbar(true);
        } finally {
          setIsSubmitting(false);
        }
      } else {
        // Original logic for other request types
        const isEditRequestType =
          request.requestTypeName === REQUEST_TYPES.LENDER_EDIT_PROJECT ||
          request.requestTypeName === REQUEST_TYPES.LENDER_EDIT_PHASE;
        const shouldIncludeStagingData = !(isEditRequestType && statusName === REQUEST_STATUSES.IN_PROGRESS);
        const shouldIncludeProjectProfile =
          request.requestTypeName === REQUEST_TYPES.LENDER_ADD_PROJECT &&
          statusName === REQUEST_STATUSES.ACTION_TAKEN &&
          projectProfileTouched &&
          projectProfileHasChanges &&
          editedProjectProfile;

        const payload = {
          requestMasterTrackerId: request.requestId,
          statusId,
          note,
          notesIsExternal: notesExternal,
          requestType: request.requestTypeName,
          assignTo: request.assignedToLdapId || passData.user,
          createdBy: passData.user,
          reviewByLdapId: passData.user,
          documentRankingList: requestedDocuments.map(dt => dt.documentRankingId),
          ...(shouldIncludeProjectProfile ? { projectProfile: editedProjectProfile } : {}),
          ...(shouldIncludeStagingData
            ? {
                stagingList: (stagingChanges || []).map(change => {
                  const originalChange = originalChanges.find(oc => oc.fieldName === change.fieldName);

                  return {
                    // projectStagingId: ?,
                    dataAttributeName: change.fieldName,
                    newValue: change.newValue,
                    oldValue: change.oldValue,
                    status: statusName === REQUEST_STATUSES.PARTIAL_ACTION_TAKEN
                      ? lineItemApprovals.get(change.fieldName)
                      : statusName === REQUEST_STATUSES.NO_ACTION_TAKEN
                      ? LINE_ITEM_STATUS.REJECT
                      : change.newValue === originalChange?.newValue
                      ? LINE_ITEM_STATUS.ACCEPT
                      : LINE_ITEM_STATUS.MODIFY,
                    requestMasterTrackerId: request.requestId,
                  };
                }),

                // todo: send entire phase once backend supports it
                // phaseStatusList: addedPhases,

                // temp for now:
                phaseStatusList: addedPhases?.map(phase => {
                  return {
                    phaseId: phase.phaseId,
                    status: phase.status
                  };
                })
              }
            : {})
        };

        try {
          const response = await axios.post(
            `${global.REACT_APP_API_ENDPOINT}/lender/request/saveReviewDetails`,
            payload
          );

          if (response.status === 200) {
            onClose();
            window.location.reload();
          } else {
            console.error("Error submitting review:", response);
            setSnackbarMessage("An error occurred while submitting the review. Please try again.");
            setShowSnackbar(true);
          }
        } catch (error) {
          console.error("Error submitting review:", error);
          setSnackbarMessage("An error occurred while submitting the review. Please try again.");
          setShowSnackbar(true);
        } finally {
          setIsSubmitting(false);
        }
      }
    }).finally(() => setIsSubmitting(false));
  }

  function phasesAreEqual(phaseA, phaseB) {
    return phaseA.phaseId === phaseB.phaseId &&
      phaseA.phaseName === phaseB.phaseName &&
      phaseA.street === phaseB.street &&
      phaseA.buildingNumber === phaseB.buildingNumber &&
      phaseA.unitNumber === phaseB.unitNumber &&
      phaseA.unitCount === phaseB.unitCount // &&
      // phaseA.city === phaseB.city &&
      // phaseA.state === phaseB.state &&
      // phaseA.zip5 === phaseB.zip5 &&
  };

  // Special case for status notes when the existing status is Additional Information Received: Instead
  // of the text of the note, show the internal/external status label.
  function getStatusNoteDisplay(note) {
    return note === "Additional documents received." && !isInternal()
          ? REQUEST_NOTES.ADDITIONAL_DOCUMENTS_SUBMITTED
          : note;
  }

  return (request && (
    <Modal
      show={show}
      backdrop="static"
      className={`request-details-modal ${
        statusName === REQUEST_STATUSES.ADDITIONAL_INFORMATION_REQUIRED ? "request-more-info" :
        statusName === REQUEST_STATUSES.ACTION_TAKEN ? "request-approved" : 
        statusName === REQUEST_STATUSES.PARTIAL_ACTION_TAKEN ? "request-partial" :
        statusName === REQUEST_STATUSES.NO_ACTION_TAKEN ? "request-rejected" : 
        statusName === REQUEST_STATUSES.UNRESPONSIVE ? "request-unresponsive" :
        ""
      }`}
      onHide={handleClose}
      size="lg"
      centered>
      <Modal.Header className="modal-header" closeButton>
        <Modal.Title className="modal-title">Request Details</Modal.Title>
      </Modal.Header>
      {show ? (
        <Modal.Body className="modal-body" style={{ position: 'relative' }}>
          {isLoadingStatusChangeData && (
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(255, 255, 255, 0.8)',
              backdropFilter: 'blur(3px)',
              zIndex: 9999,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center'
            }}>
              <div style={{ textAlign: 'center' }}>
                <div className="spinner-border text-primary" role="status" style={{ width: '3rem', height: '3rem' }}>
                  <span className="sr-only">Loading...</span>
                </div>
                <div style={{ marginTop: '15px', color: '#333', fontWeight: 'bold' }}>Loading request details...</div>
              </div>
            </div>
          )}
          <div className="request-details-section" style={{flexDirection: "row"}}>
            <div className="request-details-value">
              <b>Type:</b> {request.requestTypeName}
            </div>
            <div className="request-details-value">
              <b>Date:</b> {moment(request.requestCreatedDate).format("MM/DD/YY")}
            </div>
            {phaseName ? (
              <div className="request-details-value">
                <b>Phase Name: </b> {phaseName}
              </div>
            ) : <></>}
            {phaseId ? (
              <div className="request-details-value">
                <b>Phase ID: </b> {phaseId}
              </div>
            ) : <></>}
            {isProjectRole() || isAdminRole() ? (
              <>
                <div className="request-details-value">
                  <b>Requested By:</b> {request.requestCreatedByName || request.requestCreatedByLdapId || "(n/a)"}
                </div>
                <div className="request-details-value">
                  <b>Lender:</b> {request.lenderName || "(n/a)"}
                </div>
                <div className="request-details-value">
                  <b>Assigned To:</b> {request.assignedToName || request.assignedToLdapId || "N/A"}
                </div>
                <div className="request-details-value">
                  <b>Status: </b> 
                  {(canReview && !isProjectDeleted) ? (
                    <Dropdown className="link-dropdown" onSelect={handleStatusChange}>
                      <Dropdown.Toggle variant="link">
                        {getInternalDisplayStatus(statusName, internalStatusName)}
                      </Dropdown.Toggle>

                      
                      {/* TODO: The hardcoded labels below are a temporary solution for displaying these statuses
                      before the upcoming release, using the simplest code possible to minimize risk. After the 
                      release, both internal and external labels for all statuses will be refactored and 
                      centralized. */}
                      <Dropdown.Menu>
                        {request.statusName !== REQUEST_STATUSES.IN_PROGRESS && request.statusName !== REQUEST_STATUSES.UNDER_REVIEW && (
                          <Dropdown.Item eventKey={REQUEST_STATUSES.IN_PROGRESS}>
                            In Progress
                          </Dropdown.Item>
                        )}
                        {request.statusName !== REQUEST_STATUSES.UNRESPONSIVE && (
                          <Dropdown.Item eventKey={REQUEST_STATUSES.ADDITIONAL_INFORMATION_REQUIRED}>
                            Additional Info Required
                          </Dropdown.Item>
                        )}
                        <DropdownSubmenu title="Reviewed">
                          <Dropdown.Item eventKey={REQUEST_STATUSES.ACTION_TAKEN}>
                            Completed
                          </Dropdown.Item>
                          {(
                            request.requestTypeName === REQUEST_TYPES.LENDER_EDIT_PROJECT || 
                            request.requestTypeName === REQUEST_TYPES.LENDER_EDIT_PHASE
                          ) && (
                            <Dropdown.Item eventKey={REQUEST_STATUSES.PARTIAL_ACTION_TAKEN}>
                              Partially Completed
                            </Dropdown.Item>
                          )}
                          <Dropdown.Item eventKey={REQUEST_STATUSES.NO_ACTION_TAKEN}>
                            Declined
                          </Dropdown.Item>
                        </DropdownSubmenu>
                      </Dropdown.Menu>
                    </Dropdown>
                  ) : (
                    <span>{getInternalDisplayStatus(statusName, internalStatusName)}</span>
                  )}
                </div>
              </>
            ) : (
              <div className="request-details-value">
                <b>Status: </b> {request.externalStatus}
              </div>
            )}
            <div className="request-details-value" style={{flexBasis: '100%'}}>
              <b style={{whiteSpace: 'nowrap'}}>Status Note:</b> {getStatusNoteDisplay(statusNoteText)}
            </div>
          </div>

          {request.statusName === REQUEST_STATUSES.ADDITIONAL_INFORMATION_REQUIRED ? (
            <div className="request-details-section documents-required-section">
              <div className="request-details-column">
                {isLenderRole() || isTPORole() ? (
                  <>
                    <h3 className="request-details-subheader warning">
                      <WarningOutlined className="warning-icon" />
                      Documentation Required
                    </h3>
                    <p style={{marginBottom: 0}}>
                      <b>Fannie Mae requires the following additional documents to proceed with this request:</b>
                    </p>
                  </>
                ) : (
                  <h3 className="request-details-subheader">
                    Additional Documentation Required
                  </h3>
                )}
                {additionalDocuments.map(doc => (
                  <div key={doc.documentRankingId} className="request-doc-item">
                    <div className="request-doc-value">
                      <GrDocumentText style={{marginRight: 5}} /> 
                      {doc.documentName}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (<></>)}

          {request.statusName === REQUEST_STATUSES.UNRESPONSIVE && (isLenderRole() || isTPORole()) ? (
            <div className="request-details-section unresponsive-guidance-section">
              <div className="request-details-column">
                <h3 className="request-details-subheader warning">
                  <WarningOutlined className="warning-icon" />
                  Request Closed
                </h3>
                <p style={{ marginBottom: 0 }}>
                  <b>This request has been closed due to no response. Please submit a new request when you are ready to proceed.</b>
                </p>
              </div>
            </div>
          ) : (<></>)}

          {request.requestTypeName === REQUEST_TYPES.LENDER_ADD_PROJECT && bypassDuplicateProjectId ? (
            <div className="request-details-section bypass-duplicate-section">
              <div className="request-details-column">
                <h3 className="request-details-subheader warning">
                  <WarningOutlined className="warning-icon" />
                  Duplicate Check Bypassed
                </h3>
                <p style={{ marginBottom: 0 }}>
                  {bypassDuplicateMessage || "Duplicate check was bypassed by the lender."}{" "}
                  {isNumeric(bypassDuplicateProjectId) && !/reinstat/i.test(bypassDuplicateMessage || "") ? (
                    <RouterLink to={`/project/${bypassDuplicateProjectId}`} target="_blank" rel="noopener noreferrer">
                      View matched project
                    </RouterLink>
                  ) : null}
                </p>
              </div>
            </div>
          ) : (<></>)}

          {/* todo: replace custom styles and plain divs with responsive classes */}
          <div className="request-details-section" style={{gap: 15}}>
            {(
              request.requestTypeName === REQUEST_TYPES.REMOVE_UNAVAILABLE_STATUS 
              || request.requestTypeName === REQUEST_TYPES.ADD_UNAVAILABLE_STATUS 
              || request.requestTypeName === REQUEST_TYPES.MODIFY_UNAVAILABLE_STATUS 
            ) ? (
              <>
                {statusName === REQUEST_STATUSES.ACTION_TAKEN && canReview && !isProjectDeleted ? (
                  <div className="request-details-column">
                    <h3 className="request-details-subheader">
                      {request.requestTypeName}
                    </h3>
                    <div>
                      <div>
                        {/* <div>
                          <b>Current FM Review Status: </b> {projectInfo?.fnmaReviewStatus || "N/A"}
                        </div> */}
                        <div className="request-change-value new-value">
                          <b>New FM Review Status:</b>
                          {showFnmaStatusDropdown ? (
                            <Dropdown 
                              className={"link-dropdown field-value "} 
                              onSelect={status => handleFNMAStatusChange(status)} 
                              value={newFNMAStatus}
                            >
                              <Dropdown.Toggle variant="link">
                                {newFNMAStatus || "Select Status"}
                              </Dropdown.Toggle>
                              <Dropdown.Menu>
                                {availableFnmaStatuses.map((status) => (
                                  <Dropdown.Item key={status} eventKey={status}>
                                    {status}
                                  </Dropdown.Item>
                                ))}
                              </Dropdown.Menu>
                            </Dropdown>
                          ) : (
                            <span className={"editable-read-only field-value"}>
                              {newFNMAStatus}
                            </span>
                          )}
                        </div>
                      </div>
                      {newFNMAStatus === FM_STATUSES.APPROVED_BY_FANNIE_MAE && (
                        <div className="form-group" style={{ marginTop: '15px' }}>
                          <label style={{ fontWeight: 'bold' }}>Expiry Date <span style={{ color: 'red' }}>*</span></label>
                          <DatePicker
                            id="approvalExpiryDate"
                            label=""
                            placeholder={"Expiry Date"}
                            name={approvalExpiryDate}
                            changeHandler={(e) => {
                              if (new Date(e.target.value) >= new Date("1776-01-01")) {
                                setApprovalExpiryDate(e.target.value);
                              } else {
                                setApprovalExpiryDate(null);
                              }
                            }}
                            value={approvalExpiryDate}
                            datePickerWidth="150px"
                            min="1776-01-01"
                          />
                        </div>
                      )}
                      {hasPersDecision && newFNMAStatus === FM_STATUSES.NO_FANNIE_MAE_REVIEW && (
                        <>
                          <div className="form-group" style={{ marginTop: '15px' }}>
                            <label style={{ fontWeight: 'bold' }}>Expiration Date</label>
                            <DatePicker
                              id="suspensionExpiryDate"
                              label=""
                              placeholder={"Expiration Date"}
                              name={approvalExpiryDate}
                              changeHandler={() => {}}
                              value={approvalExpiryDate || moment().add(45, 'days').format('YYYY-MM-DD')}
                              datePickerWidth="150px"
                              min="1776-01-01"
                              disabled={true}
                            />
                          </div>
                          <div className="form-group" style={{ marginTop: '15px' }}>
                            <label style={{ fontWeight: 'bold' }}>Reason for Suspension <span style={{ color: 'red' }}>*</span></label>
                            <textarea
                              className="form-control"
                              rows="4"
                              maxLength={2000}
                              value={suspensionReason}
                              onChange={(e) => setSuspensionReason(e.target.value)}
                              placeholder="Enter reason for suspension (required)"
                              style={{ width: '100%', resize: 'vertical' }}
                            />
                            <div style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>
                              {suspensionReason.length}/2000 characters
                            </div>
                          </div>
                        </>
                      )}
                      {hasPersDecision && newFNMAStatus === FM_STATUSES.UNAVAILABLE && (
                        <>
                          <div className="form-group" style={{ marginTop: '15px' }}>
                            <label style={{ fontWeight: 'bold' }}>Selling and Servicing Guide Reference *</label>
                            <input
                              type="text"
                              className="form-control"
                              maxLength={200}
                              value={sellingServicingGuideRef}
                              onChange={(e) => setSellingServicingGuideRef(e.target.value)}
                              placeholder="Enter Selling and Servicing Guide Reference (required)"
                              style={{ width: '100%' }}
                            />
                            <div style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>
                              {sellingServicingGuideRef.length}/200 characters
                            </div>
                          </div>
                          <div className="form-group" style={{ marginTop: '15px' }}>
                            <label style={{ fontWeight: 'bold' }}>List of reviewed Project Exhibits *</label>
                            <input
                              type="text"
                              className="form-control"
                              maxLength={200}
                              value={reviewedProjectExhibits}
                              onChange={(e) => setReviewedProjectExhibits(e.target.value)}
                              placeholder="Enter List of reviewed Project Exhibits (required)"
                              style={{ width: '100%' }}
                            />
                            <div style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>
                              {reviewedProjectExhibits.length}/200 characters
                            </div>
                          </div>
                          <div className="form-group" style={{ marginTop: '15px' }}>
                            <label style={{ fontWeight: 'bold' }}>Reason for Ineligibility *</label>
                            <textarea
                              className="form-control"
                              rows="4"
                              maxLength={2000}
                              value={reasonForIneligibility}
                              onChange={(e) => setReasonForIneligibility(e.target.value)}
                              placeholder="Enter reason for ineligibility (required)"
                              style={{ width: '100%', resize: 'vertical' }}
                            />
                            <div style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>
                              {reasonForIneligibility.length}/2000 characters
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                ) : (<></>)}

                {canAddRestrictions && !isProjectDeleted ? (
                  <div className="request-details-column">                    
                    <h3 className="request-details-subheader">
                      Delivery Restrictions
                      {/* <RestrictionsTooltip restrictions={restrictions}/> */}
                    </h3>
                    <div style={{maxHeight: '100px', overflow: 'auto' }}>
                      {restrictions.map((restriction, index) => {
                        const restrictionKey = restriction.restrictionId != null
                          ? String(restriction.restrictionId)
                          : (restriction.restrictionName ?? restriction.restrictionsName);

                        const existingIndex = addedRestrictions.findIndex(r => {
                          if (r.restrictionId != null && restriction.restrictionId != null) {
                            return r.restrictionId === restriction.restrictionId;
                          }
                          return (r.restrictionName ?? r.restrictionsName) === (restriction.restrictionName ?? restriction.restrictionsName);
                        });

                        return (
                          <div key={index} style={{display: 'flex', flexDirection: 'row', gap: '10px', marginBottom: '5px' }}>                      
                            <input
                              type="checkbox"
                              className={"checkboxfield"}
                              style={{ height: 15, width: 15, marginTop: 5 }}
                              id={`restriction_${index + 1}`}
                              name={`restriction_${index + 1}`}
                              checked={existingIndex !== -1}
                              aria-label={`restriction_${index + 1}`}
                              onChange={(event) => {
                                const isSelected = event.target.checked;

                                if (isSelected && existingIndex === -1) {
                                  handleAddRestriction(restrictionKey);
                                } else if (!isSelected && existingIndex !== -1) {
                                  handleRemoveRestriction(restrictionKey);
                                }
                              }}
                            />
                            <div>
                              <div>{restriction.restrictionName ?? restriction.restrictionsName}</div>
                              <div>{restriction.restrictionDescription ?? restriction.restrictionsDescription}</div>
                            </div>
                          </div>
                        )}) ?? <></>}
                    </div>
                  </div>
                ) : (<></>)}

                {((request.requestTypeName === REQUEST_TYPES.ADD_UNAVAILABLE_STATUS || request.requestTypeName === REQUEST_TYPES.MODIFY_UNAVAILABLE_STATUS || request.requestTypeName === REQUEST_TYPES.REMOVE_UNAVAILABLE_STATUS) && (!canReview || shouldShowReasonCodes || statusName !== REQUEST_STATUSES.ACTION_TAKEN)) ? (
                  useReasonCodeDeltaModel ? (
                    <div style={{marginTop: '10px' }}>
                      <ReasonCodesReviewSection
                        activeReasonCodeIds={deltaActiveReasonCodeIds}
                        requestStatusChangeReasonCodes={deltaReasonCodeChanges}
                        reasonCodeOptions={allReasonCodes}
                        showDecisionStatus={!(canAddReasonCodes && !isProjectDeleted)}
                        canReviewReasonCodes={canAddReasonCodes && !isProjectDeleted}
                        onReasonCodeDecisionsChange={setReasonCodeDecisions}
                        statusName={statusName}
                      />
                    </div>
                  ) : (
                  <div style={{marginTop: '10px' }}>
                    {/* Active Reason Codes - shown for Modify Unavailable only */}
                    {request.requestTypeName === REQUEST_TYPES.MODIFY_UNAVAILABLE_STATUS && activeReasonCodes?.length > 0 && (
                      <>
                        <h3 className="request-details-subheader">
                           Current Reason Codes
                          <ReasonCodeTooltip reasonCodes={allReasonCodes}/>
                        </h3>
                        <div className="reason-codes-readonly" style={{ marginBottom: '15px' }}>
                          {activeReasonCodes.map((rc, index) => (
                            <span key={index} className="reason-code-badge">
                              {rc.reasonCode}
                            </span>
                          ))}
                        </div>
                      </>
                    )}
                    <h3 className="request-details-subheader">
                      {request.requestTypeName === REQUEST_TYPES.MODIFY_UNAVAILABLE_STATUS ? 'Requested Reason Codes' : 'Reason Codes'}
                      {request.requestTypeName !== REQUEST_TYPES.MODIFY_UNAVAILABLE_STATUS && <ReasonCodeTooltip reasonCodes={allReasonCodes}/>}
                    </h3>
                    {canAddReasonCodes && !isProjectDeleted ? (
                      <div>
                        <ReactSelect
                          name="reasonCodes"
                          className="pers-reason-codes"
                          options={reasonCodeOptions}
                          isMulti
                          closeMenuOnSelect={false}
                          hideSelectedOptions={false}
                          value={addedReasonCodes?.map(rc => ({ value: rc.reasonId, label: rc.reasonCode })) ?? []}
                          onChange={(values) => handleAddReasonCodes(values)}
                          allowSelectAll={false}
                        />
                      </div>
                    ) : (
                      // Display selected reason codes as a read-only list if user cannot add/remove reason codes
                      <div className="reason-codes-readonly">
                        {addedReasonCodes.length ? (
                          addedReasonCodes.map((rc, index) => (
                            <span key={index} className="reason-code-badge">
                              {rc.reasonCode}
                            </span>
                          ))
                        ) : (
                          <div className="no-reason-codes">(none)</div>
                        )}
                      </div>
                    )}
                  </div>
                  )
                ) : (<></>)}
              </>
            ) : (request.requestTypeName === REQUEST_TYPES.LENDER_EDIT_PHASE || request.requestTypeName === REQUEST_TYPES.LENDER_EDIT_PROJECT) && stagingChanges?.length ? (
              <div className="request-details-column">
                <h3 className="request-details-subheader">
                  Requested Changes
                </h3>
                <div className="request-change-list">
                  {stagingChanges.map((change, changeIndex) => {   
                    const fieldName = change.fieldName;
                    const className = change.def?.type === "text"
                      ? "request-field-input"
                      : "field-value";
                      
                    // if this is styleComments:
                    // if the projectStyle change is being rejected, styleComments must also be rejected
                    // if the projectStyle change is being modified to "Other", styleComments must also be modified
                    const mustReject = fieldName === "styleComments" && lineItemApprovals.get("projectStyle") === LINE_ITEM_STATUS.REJECT;
                    const mustModify = fieldName === "styleComments" && lineItemApprovals.get("projectStyle") === LINE_ITEM_STATUS.MODIFY
                      && stagingChanges.find(fc => fc.fieldName === "projectStyle")?.newValue === "Other";

                    return (
                      <div key={changeIndex} className="request-change">
                        <div className="request-change-value field-name">
                          <span>{change.label}</span>
                        </div>
                        <div className="request-change-value old-value">
                          <b>Old Value:</b>
                          <span>{change.formatValue(change.oldValue) || "(none)"}</span>
                        </div>
                        <div className="request-change-value new-value">
                          <b>
                            New Value:
                          </b>
                          <EditableRequestField
                            className={className}
                            fieldDef={change.def}
                            fieldValue={change.newValue}
                            inputRef={
                              change.fieldName === "street" ? streetInputRef :
                              change.fieldName === "city" ? cityInputRef :
                              change.fieldName === "zip5" ? zip5InputRef :
                              undefined
                            }
                            onChanged={(modifiedValue, validationMessage) => handleValueModified(change.fieldName, modifiedValue, validationMessage, changeIndex)}
                            isEditing={editingItems.has(change.fieldName)}
                            onBlur={() => {}}
                            // onFocus={(validationMessage) => handleFocus(fieldName, validationMessage)}
                          />
                          {(canReview && !isProjectDeleted) ? (
                            statusName === "Action Taken" ? (
                              <div className="request-change-value request-change-approval">
                                <b>&nbsp;</b>
                                {editingItems.has(change.fieldName)
                                  ? <Link className="lnk-modify" disabled={mustModify} onClick={e => cancelFieldEditing(e, change.fieldName)}>Cancel</Link>
                                  : <Link className="lnk-modify" onClick={e => handleFieldEditing(e, change.fieldName)}>Modify</Link>
                                }
                              </div>
                            ) : statusName === REQUEST_STATUSES.PARTIAL_ACTION_TAKEN ? (
                              <div className="request-change-value request-change-approval">
                                <Dropdown className="link-dropdown" onSelect={status => handleLineItemStatus(change.fieldName, status)}>
                                  <Dropdown.Toggle variant="link" disabled={mustReject || mustModify}>
                                    {lineItemApprovals.has(change.fieldName)
                                      ? lineItemApprovals.get(change.fieldName)
                                      : "Accept/Reject/Modify"
                                    }
                                  </Dropdown.Toggle>
                                  <Dropdown.Menu>
                                    <Dropdown.Item eventKey={LINE_ITEM_STATUS.ACCEPT}>Accept</Dropdown.Item>
                                    <Dropdown.Item eventKey={LINE_ITEM_STATUS.REJECT}>Reject</Dropdown.Item>
                                    <Dropdown.Item eventKey={LINE_ITEM_STATUS.MODIFY}>Modify</Dropdown.Item>
                                  </Dropdown.Menu>
                                </Dropdown>
                              </div>
                            ) : (<></>)
                          ) : (<></>)}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
              ) : (<></>)}
          </div>

          {addedPhases?.length ? (
            <div className="request-details-section" style={{gap: 15}}>
              <div className="request-details-column">
                <h3 className="request-details-subheader">
                  Requested Phases
                </h3>
                {currentProjectType === PROJECT_TYPES.ESTABLISHED_CONDO && (
                  <div style={{
                    padding: '12px',
                    marginBottom: '15px',
                    backgroundColor: '#e7f3ff',
                    border: '1px solid #b3d9ff',
                    borderRadius: '4px',
                    color: '#004085',
                    fontSize: '0.9em'
                  }}>
                    <strong>ℹ️ Established Project:</strong> Phases are only applicable to New Projects. Phase requests for Established Projects will be automatically rejected.{(isLenderRole() || isTPORole()) && ' If the project meets the definition of a New Project, submit an "Edit Project" request.'}
                  </div>
                )}
                <div className="request-change-list">
                  {addedPhases.map((phase, phaseIndex) => {   
                    const isPhaseAutoRejected = currentProjectType === PROJECT_TYPES.ESTABLISHED_CONDO && isClosedStatus(request.statusName);
                    
                    return (
                      <div key={phaseIndex} className="request-change">
                        <div className="request-change-value field-name">
                          <span>{phase.phaseName || "(no phase name)"}</span>
                          {isPhaseAutoRejected && !isLenderRole() && (
                            <span style={{
                              marginLeft: '10px',
                              padding: '2px 8px',
                              backgroundColor: '#f8d7da',
                              color: '#721c24',
                              borderRadius: '4px',
                              fontSize: '0.85em',
                              fontWeight: 'bold',
                              border: '1px solid #f5c6cb'
                            }}>
                              Auto-Rejected
                            </span>
                          )}
                          {(statusName === REQUEST_STATUSES.ACTION_TAKEN || statusName === REQUEST_STATUSES.PARTIAL_ACTION_TAKEN) 
                            && canReview && !isProjectDeleted ? (
                              <div className="request-change-value request-change-approval">
                                <Dropdown className="link-dropdown" onSelect={value => {
                                    if (phase.status === value) return;
                                    if (phase.status === LINE_ITEM_STATUS.MODIFY && value === LINE_ITEM_STATUS.ACCEPT) return;

                                    addedPhases[phaseIndex] = {
                                      ...originalAddedPhases[phaseIndex],
                                      status: value
                                    };
                                    setAddedPhases([...addedPhases]);

                                    phaseEditingItems.delete(phaseIndex);
                                    setPhaseEditingItems(new Map(phaseEditingItems));

                                    phaseValidationErrors.delete(phaseIndex);
                                    setPhaseValidationErrors(new Map(phaseValidationErrors));
                                  }}>
                                  <Dropdown.Toggle variant="link" disabled={currentProjectType === PROJECT_TYPES.ESTABLISHED_CONDO}>
                                    {/* {phase.status} */}
                                    {!phase.status 
                                      ? "Accept/Reject"
                                      : phase.status === LINE_ITEM_STATUS.MODIFY
                                      ? "Accept"
                                      : phase.status}
                                  </Dropdown.Toggle>
                                  <Dropdown.Menu>
                                    <Dropdown.Item eventKey={LINE_ITEM_STATUS.ACCEPT}>Accept</Dropdown.Item>
                                    <Dropdown.Item eventKey={LINE_ITEM_STATUS.REJECT}>Reject</Dropdown.Item>
                                  </Dropdown.Menu>
                                </Dropdown>
                              </div>
                            ) : (<></>)}
                        </div>
                        {["street", "buildingNumber", "unitNumber", "unitCount"]
                          .map(fieldName => {
                            const resolvedFieldDef = phaseFieldsMap.get(fieldName);
                            const fieldDef = resolvedFieldDef || createFallbackFieldDef(fieldName);
                            const className = fieldDef?.type === "text"
                              ? "request-field-input"
                              : "field-value";
                            const canEdit = phase.status === LINE_ITEM_STATUS.ACCEPT || phase.status === LINE_ITEM_STATUS.MODIFY;
                            const isEditing = phaseEditingItems.get(phaseIndex)?.has(fieldName);

                            return (
                              <div key={`phase-${phaseIndex}-${fieldName}`} className="request-change-value new-value">
                                <b>{fieldDef.formatLabel()}: </b>
                                <EditableRequestField
                                  className={className}
                                  fieldDef={fieldDef}
                                  fieldValue={phase[fieldName]}
                                  inputRef={fieldName === "street" ? (ref => { if (ref) phaseStreetRefs.current[phaseIndex] = ref; }) : undefined}
                                  onChanged={(modifiedValue, validationMessage) => handlePhaseValueModified(phaseIndex, fieldName, modifiedValue, validationMessage)}
                                  isEditing={isEditing}
                                  onBlur={() => {}}
                                  // onFocus={(validationMessage) => handlePhaseFocus(i, fieldName, validationMessage)}
                                />
                              </div>
                            );
                          })}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          ) : (<></>)}

          {request.requestTypeName === REQUEST_TYPES.LENDER_ADD_PROJECT && (
            <div className="request-details-section project-info-full-width">
              <div className="request-details-column">
                <ProjectInfoEditorAccordion
                  projectInfo={fetchedProjectProfile || projectInfo?.projectProfile || projectInfo || {}}
                  fieldsMap={projectFieldsMap}
                  onFieldsChanged={handleProjectProfileChanged}
                  readOnly={isReadOnlyMode}
                  defaultExpanded={false}
                  addressAutocompleteRefs={{
                    streetRef: streetInputRef,
                    cityRef: cityInputRef,
                    zip5Ref: zip5InputRef,
                  }}
                  onAddressFieldsUpdate={handleAddressFieldsUpdate}
                  externalFieldUpdates={autocompleteFieldUpdates}
                  onExpandedChange={setAccordionExpanded}
                  title="Project Info"
                />
              </div>
            </div>
          )}

          {/* Remove Delivery Restriction Review Section */}
          {request.requestTypeName === REQUEST_TYPES.REMOVE_DELIVERY_RESTRICTION && (
            <div className="request-details-section review-delivery-restrictions">
              <DeliveryRestrictionsReviewSection
                request={request}
                currentRestrictionIDsFromRequest={request.currentDrDetails}
                currentRestrictionIDs={currentRestrictionIds}
                requestedRestrictionIDs={requestedRestrictionIds}
                removeDrTrackers={fetchedRemoveDRTrackers}
                showDecisionStatus={!canReviewRestrictions && isRemoveDRDecisionStatus}
                showCombinedCurrentAndRequested={isRemoveDRDecisionStatus}
                showRequestedOnly={!isRemoveDRDecisionStatus && !canReviewRestrictions}
                showDecisionColumn={!isLenderRole() && !isTPORole()}
                showFinalRestrictions={showRemoveDRFinalRestrictions}
                isRequestDeclined={statusName === REQUEST_STATUSES.NO_ACTION_TAKEN && canReviewRestrictions}
                onSelectRestrictions={restrictionIds => setDeliveryRestrictionDecisions(restrictionIds)}
                canReviewRestrictions={canReviewRestrictions && !isProjectDeleted}
                historicMode={isClosedStatus(request.statusName)}
              />
            </div>
          )}


          <div className="request-details-section">
            <div className="request-details-column">
              <h3 className="request-details-subheader">Attachments</h3>
              {canReview && !isProjectDeleted && statusName === REQUEST_STATUSES.ADDITIONAL_INFORMATION_REQUIRED ? (
                <div className="requested-doc-select">
                  <label style={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>Requested Documents</label>
                  <ReactSelect
                    name="requestedDocuments"
                    className="pers-reason-codes"
                    options={availableDocuments.map(doc => ({ value: doc.documentRankingId, label: doc.documentName }))}
                    isMulti
                    closeMenuOnSelect={false}
                    hideSelectedOptions={false}
                    placeholder="Select documents..."
                    value={requestedDocuments.map(doc => ({ value: doc.documentRankingId, label: doc.documentName }))}
                    onChange={(values) => {
                      const selectedIds = new Set((values || []).map(v => v.value));
                      const newRequested = availableDocuments
                        .filter(doc => selectedIds.has(doc.documentRankingId))
                        .map(doc => ({ ...doc, documentName: doc.documentName ?? doc.name }));
                      setRequestedDocuments(newRequested);
                    }}
                    allowSelectAll={false}
                  />
                </div>
              ) : (<></>)}
              {documents?.map((docItem, i) => (
                <div key={i} className="request-doc-item">
                  <div className="request-doc-value">
                    <b>{docItem.documentName}: </b>
                    <a
                      className="pdf-link"
                      onClick={() => downloadFile(docItem.docMapId, docItem.s3ObjectName)}
                      style={{ 
                        cursor: "pointer", 
                        color: "#007bff", 
                        textDecoration: "underline" 
                      }}
                    >
                    {docItem.documentName}
                    </a>
                  </div>
                  <div className="request-doc-value comment-body">
                    <b>Note: </b>
                    {docItem.notes || "(none)"}
                  </div>
                </div>
              )) ?? <></>}
            </div>
          </div>
          
          {canReview && !isProjectDeleted && (
            <div className="request-details-section" style={{gap: 15}}>
              <div className="request-details-column" style={{gap: 0}}>
                <h3 className="request-details-subheader">
                  Notes
                  {isOtherRequested && (
                    <span style={{ color: 'red' }}>*</span>
                  )}
                </h3>
                <div className="form-group mt-3" style={{ position: "relative", paddingBottom: notesExternal === "both" ? "50px" : "0px" }}>
                  {/* <label htmlFor="notes">Notes</label> */}
                  <div
                    className={`notes-box${notesBoxActive ? " notes-box-active" : ""}`}
                    style={{ position: "relative", width: "95%", minWidth: 0, background: '#fff', borderRadius: '4px', display: 'flex', flexDirection: 'column' }}
                  >
                    <TextareaAutosize
                      id="notes"
                      minRows={3}
                      placeholder="Add notes here..."
                      value={note}
                      onChange={(e) => {
                        setNote(e.target.value);
                        setNotesBoxActive(true);
                      }}
                      onFocus={() => setNotesBoxActive(true)}
                      onBlur={() => setNotesBoxActive(false)}
                      style={{ width: "100%", marginTop: "5px", border: 'none', outline: 'none', resize: 'vertical', background: 'transparent', maxHeight: '120px', overflow: 'auto', flex: '1 1 auto' }}
                    />
                    <div className="toggle-container" style={{ marginTop: '8px', alignSelf: 'flex-end' }}>
                      <ToggleButtonGroup
                        color="primary"
                        value={notesExternal ? "both" : "internal"}
                        exclusive
                        onChange={(event, value) => {
                          setNotesExternal(value === "both");
                        }}
                        aria-label="text alignment"
                        size="small"
                      >
                        <ToggleButton value="internal" aria-label="left aligned">
                          Internal
                        </ToggleButton>
                        <ToggleButton value="both" aria-label="centered">
                          Internal & External
                        </ToggleButton>
                      </ToggleButtonGroup>
                    </div>
                  </div>
                  {notesExternal && (
                    <div className="comment-note" style={{ marginTop: "8px", color: '#1176B5', fontWeight: 'bold', background: '#fff', padding: '4px 8px', borderRadius: '4px', width: '100%' }}>
                      This will allow lenders to view this note; use standardized language when possible.
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </Modal.Body>
      ) : (<></>)}
      <Modal.Footer className="modal-footer">
        {(canReview && !isProjectDeleted) ? (
          <>
            <Button className="cancel-button cancel-modal-button" onClick={handleClose}>
              Cancel
            </Button>
            <Button className="btn-submit" 
              style={{width: "unset"}} 
              disabled={!canSubmit || isSubmitting} 
              onClick={handleSubmit}>
              Submit
            </Button>
          </>
        ) : (
          <>
            <Button className="cancel-button cancel-modal-button" onClick={handleClose}>
              Close
            </Button>
            {!isProjectDeleted && (isLenderRole() || isTPORole()) &&
              !isReadonlyRole() &&
              isLenderActionableStatus(statusName) && (
                <Button
                  className="btn-submit"
                  style={{width: "unset"}}
                  href={`/lender-project-request/additional-documents/${request.projectId}/${request.requestId}`}>
                  Add Documents
                </Button>
              )}
          </>
        )}
      </Modal.Footer>
      
      <Snackbar 
        open={showSnackbar} 
        autoHideDuration={6000} 
        onClose={(event, reason) => {
          if (reason === 'clickaway') return;
          setShowSnackbar(false);
        }}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setShowSnackbar(false)} 
          severity="error" 
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Modal>
  ));
};

export default RequestDetailsModal;
